import React, { useEffect, useState, useContext } from 'react'
import { useLoaderData, useSearchParams, useParams } from 'react-router-dom'
import { useAuth } from '../../../../../hooks/auth'
import {
    Items,
    LongTermVolumes,
    PlanPosition,
    ItemDetail,
    PositionFinancialProvisionVolumes,
} from '../types'

import { Plan } from '../../types'
import PurchaseBasicInfo from './components/PurchaseBasicInfo'
import PurchaseContractInfo from './components/PurchaseContractInfo'
import PurchaseBudgetFunds from './components/PurchaseBudgetFunds'
import PurchaseDatesInfo from './components/PurchaseDatesInfo'
import PurchaseItemsSection from './components/PurchaseItemsSection'
import PurchaseMethod from './components/PurchaseMethod'
import PurchasePaymentBreakdown from './components/PurchasePaymentBreakdown'

import Footer from './Footer'
import PurchaseChangeInfo from './components/PurchaseChangeInfo'
import { Structure } from '../../../Org/Structure/types'
import { Role } from '../../../Org/Roles/types'
import {
    PlanPositionRow,
    Approvals,
    Approvers,
    ApproversAdd,
    PurchaseContractErrors,
    PurchaseMethodErrors,
    PurchaseEditErrors,
    PurchaseChangeInfoErrors,
    PurchaseBudgetFundsErrors,
    PurchaseBudgetFundsItemErrors,
    PurchasePaymentBreakdownErrors,
    PurchaseItemsSectionErrors,
    PurchaseItemRowErrors,
    PurchaseDatesInfoErrors,
    CurrencyList,
    CurrencyRate,
} from './types'

import { User } from '../../../Org/Users/types'
import PositionsStage from './components/PositionsStage'
import PositionsInfo from './components/PositionsInfo'
import { OrganizationInfo } from '../../../Org/Home/types'

import { ThemeContext } from '../../../../../components/ThemeContext'

import HoverTooltip from '../../../../../components/HoverTooltip'

// import ApprovalsList from './components/Approval/ApprovalsList'

type Item = {
    summ: number
    year: number
    summ_rub?: number
}

type ApiResponse = {
    message: User[]
}

type ApiResponsePlan = {
    message: Plan[]
}

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

export const loadPlanPosition = async ({
    params,
}: any): Promise<PlanPosition | null> => {
    const action = params.action
    const isDraft =
        action === 'new' || action === 'draft' || action === 'approve'

    let url = process.env.REACT_APP_API_URL + '/api/backend/v2/'

    if (isDraft) {
        if (action == 'approve') {
            url += 'plan_position_draft_for_approve'
        } else {
            url += 'plan_position_draft'
        }
    } else {
        url += 'plan_positions'
    }

    url += '/?reg_number=' + params.plan_reg_number

    if (action !== 'new') {
        url += '&guid=' + params.pos_guid
    }

    return fetch(url, {
        method: action === 'new' ? 'POST' : 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Failed to fetch plan positions')
            }
        })
        .then(
            (data) =>
                (isDraft
                    ? action === 'new'
                        ? data.plan_pos
                        : data
                    : data[0]) as Promise<PlanPosition>
        )
        .catch(() => null)
}

function getCurrentYear() {
    const today = new Date()
    return today.getFullYear() // Возвращает текущий год
}

function getCurrentMonth() {
    const today = new Date()
    return today.getMonth() // Возвращает номер месяца (0–11)
}

function getQuarterByMonth(month: number) {
    return Math.ceil(month / 3)
}

function getStartDate(pos: PlanPosition): Date {
    let date: Date
    if (pos.purchase_planned_date !== null) {
        date = new Date(pos.purchase_planned_date)
    } else if (
        pos.purchase_period_year !== null &&
        pos.purchase_period_month !== null
    ) {
        date = new Date(pos.purchase_period_year, pos.purchase_period_month - 1)
    } else {
        date = new Date()
    }
    return date
}

function getUpdatedLongTermVolumes(
    pos: PlanPosition,
    existingVolumes: LongTermVolumes | null,
    yearsInRange: number[]
): LongTermVolumes {
    if (existingVolumes === null) {
        return {
            volume: 0,
            currency: {
                code: pos.currency_code,
                digital_code: pos.currency_digital_code,
                name: pos.currency_name,
            },
            details: {
                items: yearsInRange.map((year) => ({
                    summ: 0,
                    year: year,
                })),
            },
            volume_rub: undefined,
            exchange_rate: null,
            exchange_rate_date: null,
        }
    } else {
        const existingItemsSource = existingVolumes.details?.items ?? []

        const existingYears = existingItemsSource.map((item) => item.year)

        const newItems = yearsInRange
            .filter((year) => !existingYears.includes(year))
            .map((year) => ({
                summ: 0,
                year: year,
            }))

        const existingItems = existingItemsSource.filter((item) =>
            yearsInRange.includes(item.year)
        )

        const allItems = [...newItems, ...existingItems].sort(
            (a, b) => a.year - b.year
        )

        const volume = existingItems
            .map((item) => item.summ)
            .reduce((a, b) => Math.round((a + b) * 100) / 100, 0)

        return {
            volume: volume,
            currency: {
                code: pos.currency_code,
                digital_code: pos.currency_digital_code,
                name: pos.currency_name,
            },
            details: {
                items: allItems,
            },
            volume_rub: existingVolumes.volume_rub,
            exchange_rate: existingVolumes.exchange_rate,
            exchange_rate_date: existingVolumes.exchange_rate_date,
        }
    }
}

function onDateUpdate(
    pos: PlanPosition,
    isStart: boolean,
    newDate: Date,
    isFullDate?: boolean,
    reportingYear?: number
): PlanPosition {
    const getYearsInRange = (start: Date, end: Date) => {
        const startYear = start.getFullYear()
        const endYear = end.getFullYear()
        return Array.from(
            { length: endYear - startYear + 1 },
            (_, i) => startYear + i
        )
    }

    const startDate = isStart ? newDate : getStartDate(pos)
    const endDate = isStart
        ? pos.contract_end_date
            ? new Date(pos.contract_end_date)
            : new Date(getCurrentYear(), getCurrentMonth(), 1)
        : newDate

    const yearsInRange = getYearsInRange(startDate, endDate)

    console.log('yearsInRange - ' + yearsInRange)

    const repDate = reportingYear ? reportingYear : getCurrentYear()

    // Условие обнуления: если оба года совпадают с годом плана
    const shouldClearLongTerm =
        (startDate.getFullYear() === reportingYear &&
            endDate.getFullYear() === reportingYear) ||
        (startDate.getFullYear() - repDate >= 1 && pos.is_purchase_smb)

    const newLongTermVolumes = !shouldClearLongTerm
        ? getUpdatedLongTermVolumes(pos, pos.long_term_volumes, yearsInRange)
        : null

    const newLongTermSmbVolumes = !shouldClearLongTerm
        ? getUpdatedLongTermVolumes(
              pos,
              pos.long_term_smb_volumes,
              yearsInRange
          )
        : null

    return {
        ...pos,
        purchase_planned_date: isStart
            ? isFullDate
                ? `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`
                : null
            : pos.purchase_planned_date,
        purchase_period_month: isStart
            ? isFullDate
                ? null
                : newDate.getMonth() + 1
            : pos.purchase_period_month,
        purchase_period_year: isStart
            ? isFullDate
                ? null
                : newDate.getFullYear()
            : pos.purchase_period_year,
        purchase_period_quarter: isStart
            ? isFullDate
                ? null
                : getQuarterByMonth(newDate.getMonth() + 1)
            : pos.purchase_period_quarter,
        contract_end_date: isStart
            ? pos.contract_end_date
            : `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`,
        long_term_volumes: newLongTermVolumes,
        long_term_smb_volumes: newLongTermSmbVolumes,
    }
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

const getUserList = async (): Promise<User[]> => {
    return fetch(
        process.env.REACT_APP_API_URL +
            '/api/backend/v1/organization/users_fio',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Failed to fetch organization')
            }
        })
        .then((response: ApiResponse) => response.message)
        .catch((e) => {
            console.error(e)
            return []
        })
}

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const getCurrentDateTime = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // Добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    const hours = String(today.getHours()).padStart(2, '0') // Часы
    const minutes = String(today.getMinutes()).padStart(2, '0') // Минуты
    const seconds = String(today.getSeconds()).padStart(2, '0') // Секунды
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

function formatDateTime(input: string): string {
    const date = new Date(input)

    const day = date.getDate()
    const month = date.toLocaleString('ru-RU', { month: 'long' })
    const year = date.getFullYear()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${day} ${capitalize(month)} ${year} ${hours}:${minutes}`
}

const PlanPositionEditPage: React.FC = () => {
    const [pos, setPos] = useState(useLoaderData() as PlanPosition)
    const [purchaseItemsRows, setPurchaseItemsRows] = useState<
        PlanPositionRow[]
    >([])

    const [errors, setErrors] = useState<PurchaseEditErrors>({})
    const [errorPopup, setErrorPopup] = useState(false)
    const [isHaveError, setHaveError] = useState<boolean>(true)

    const { user } = useAuth()

    const { theme } = useContext(ThemeContext)

    const [loading, setLoading] = useState<boolean>(false)
    const [creating, setCreating] = useState<boolean>(false)
    const [deleting, setDeleting] = useState<boolean>(false)

    const { plan_reg_number: reg_number } = useParams()
    // const [planGuidNav, setPlanGuidNav] = useState(routePlanGuid)

    const [isLoadingPlan, setLoadingPlan] = useState<boolean>(true)
    const [plan, setPlan] = useState<Plan[]>([])
    const [isSendPlan, setSendPlan] = useState<boolean>(false)
    const [planYear, setPlanYear] = useState<string>('')

    const [isUserStructGuid, setUserStructGuid] = useState('')
    const [isUserStructName, setUserStructName] = useState('')

    const [isUserRoleName, setUserRoleName] = useState('')

    const [structList, setStructList] = useState<Structure[]>([])

    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])
    const [currencyRate, setCurrencyRate] = useState<CurrencyRate>()

    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const [approvals, setApprovals] = useState<Approvals[]>([])
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

    const [lastApprovalStatus, setLastApprovalStatus] = useState<boolean>(false)
    const [lastApprovalStatusAccept, setLastApprovalStatusAccept] =
        useState<boolean>(false)
    const [activeApproverStatus, setActiveApproverStatus] =
        useState<boolean>(false)

    const [approvers, setApprovers] = useState<Approvers[]>([])
    const [userApprovers, setUserApprovers] = useState<Approvers[]>([])

    const [saveLoading, setSaveLoading] = useState<boolean>(false)
    const [selectedStatus, setSelectedStatus] = useState<number | null>(null)
    const [comment, setComment] = useState<string>('')

    const [approvalCreatePopup, setApprovalCreatePopup] = useState(false)
    const [isApprovalCreate, setApprovalCreate] = useState(false) // Состояние для анимации загрузки

    const [approvalDeletePopup, setApprovalDeletePopup] =
        useState<boolean>(false)
    const [approvalDeleteGuid, setApprovalDeleteGuid] = useState('')

    const [approvalStartDate, setApprovalStartDate] = useState(getCurrentDate())
    const [approvalEndDate, setApprovalEndDate] = useState('')
    const [approvalPriority, setApprovalPriority] = useState(0)
    const [approvalComment, setApprovalComment] = useState('')

    const [userList, setUserList] = useState<User[]>([])
    const [userFilteredList, setUserFilteredList] = useState<User[]>([])
    const [addApproversPopup, setAddApproversPopup] = useState(false)
    const [isApprovalsAdd, setApprovalAdd] = useState(false) // Состояние для анимации загрузки

    const [originalUserList, setOriginalUserList] = useState<User[]>([]) // Сохраняем исходный список пользователей

    const [approversAdd, setApproversAdd] = useState<ApproversAdd[]>([])

    const [selectedItems, setSelectedItems] = useState<string[]>([])

    const [userFilter, setUserFilter] = useState<string[]>([]) // Массив строк

    const [changeApprovalsPopup, setChangeApprovalsPopup] = useState(false)
    const [isApprovalChanging, setApprovalChanging] = useState(false) // Состояние для анимации загрузки
    const [approvalChangeGuid, setApprovalChangeGuid] = useState('')
    const [approvalAddGuid, setApprovalAddGuid] = useState('')

    const isNew = location.hash.includes('new')

    const isApprove = location.hash.includes('approve')

    const [userApprover, setUserApprover] = useState<Approvers>()

    const [isOkpd2Have, setOkpd2Have] = useState<boolean>(false)
    const [isHaveNotMspOkpd2, setHaveNotMspOkpd2] = useState<boolean>(false)

    const [infoTab, setInfoTab] = useState<string>('approval')
    const [changeSupervisorPopup, setChangeSupervisorPopup] = useState(false)
    const [selectedSupervisor, setSelectedSupervisor] = useState<string>('')
    const [supervisorChanged, setSupervisorChanged] = useState(false)

    const handleOkpd2Have = (value: boolean) => {
        setOkpd2Have(value)
    }

    const handleHaveNotMspOkpd2 = (value: boolean) => {
        setHaveNotMspOkpd2(value)
    }

    const getNameStruct = (struct_guid: string) => {
        const nameSctruct = structList.find((s) => s.guid === struct_guid)
        return nameSctruct?.struct_short_name || '-'
    }

    const getNameUser = (user_guid: string) => {
        const nameUser = userList.find((u) => u.guid === user_guid)
        return (
            `${nameUser?.last_name} ${nameUser?.first_name} ${nameUser?.patronymic}` ||
            '-'
        )
    }

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const loadPlan = () => {
        setLoadingPlan(true)

        fetch(process.env.REACT_APP_API_URL + '/api/backend/v2/plans', {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to fetch plan')
                }
            })
            .then((response: ApiResponsePlan) => response.message)
            .catch((e) => {
                console.error(e)
                return []
            })
            .then((response) => {
                setPlan(
                    response.filter(
                        (plans: Plan) =>
                            String(plans.registration_number) === reg_number
                    )
                )

                const planData = response.filter(
                    (plans: Plan) =>
                        String(plans.registration_number) === reg_number
                )
                planData.map((item: Plan) => {
                    setSendPlan(item.is_sent)
                    setPlanYear(item.start_date.slice(0, 4))
                })
            })
            .finally(() => {
                setLoadingPlan(false)
            })
    }

    const getCurrencyList = async (): Promise<CurrencyList[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/currency',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на курсы валют')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getCurrencyRateByDate = async (
        num_code: string,
        currency_date: string
    ): Promise<CurrencyRate> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/currency_rate/by_code_and_date?num_code=${num_code}&currency_date=${currency_date}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    console.log('Отсутствует курс валюты на конкретную дату')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const parseCurrencyCbr = async (
        date: string
    ): Promise<{ message: string } | null> => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/currency_rate/fetch_from_cbr?date=${date}`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (response.ok) {
                return await response.json()
            } else {
                console.error('Ошибка при запросе парсинга курсов ЦБ')
                return null
            }
        } catch (e) {
            console.error('Ошибка сети при парсинге курсов ЦБ:', e)
            return null
        }
    }

    const getApprovals = async (pos_guid: string): Promise<Approvals[]> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/approvals_by_pos_draft_guid?plan_pos_guid=${pos_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса по согласованию')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getApprovers = async (approvalGuid: string): Promise<Approvers[]> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/approvers_by_approval/${approvalGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса по согласующим')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const updateApproverSatatus = async (
        approval_guid: string,
        approver_guid: string
    ) => {
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/update_db_approver_is_saw?approver_guid=${approver_guid}`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            if (!response.ok) {
                throw new Error('Обновления просмотра согласующим')
            }
            console.log('Просмотр обновлен')
        } catch (error) {
            console.error('Ошибка при обновлении просмотра согласующим:', error)
        } finally {
            const updatedApprovers = await getApprovers(approval_guid)
            setApprovers(updatedApprovers)
        }
    }

    // Проверка статуса согласования для отображения кнопки "Редактировать" и "Удалить"
    const getApprovalSatus = async (approval: Approvals[]) => {
        if (!approval.length) {
            setLastApprovalStatus(true)
            setLastApprovalStatusAccept(false)
            return
        }

        const lastApproval = approval.reduce((latest, current) => {
            return new Date(current.create_date) > new Date(latest.create_date)
                ? current
                : latest
        })

        const isApproved = lastApproval.status !== 0
        setLastApprovalStatus(isApproved)

        const isApprovedStatusAccept = lastApproval.status === 1
        setLastApprovalStatusAccept(isApprovedStatusAccept)

        // Если статус последний — 0, проверяем согласующих
        if (!isApproved) {
            try {
                const approver = await getApprovers(lastApproval.guid)

                if (!approver || approver.length === 0) {
                    setActiveApproverStatus(false)
                } else {
                    const anyApproved = approver.some((a) => a.status !== 0)
                    setActiveApproverStatus(anyApproved)
                }
            } catch (error) {
                console.error('Ошибка при получении согласующих:', error)
                setActiveApproverStatus(false)
            }
        } else {
            setActiveApproverStatus(false)
        }
    }

    useEffect(() => {
        getApprovalSatus(approvals)
        const fetchData = async () => {
            try {
                const organization = await loadOrganizationData()
                if (organization) {
                    setOrgData(organization)
                }

                const role = await getRole(user?.object.role_id)
                setUserRoleName(role.name)

                const users = await getUserList()
                if (Array.isArray(users)) {
                    setOriginalUserList(users) // Сохраняем исходный список пользователей
                    setUserList(users)

                    // Начальная фильтрация userFilteredList
                    const approversGuids = new Set(
                        approversAdd.map((approver) => approver.user_guid)
                    )
                    const filteredUsers = users.filter(
                        (u) => !approversGuids.has(String(u.guid))
                    )
                    setUserFilteredList(filteredUsers)
                } else {
                    console.error('Нет данных:', users)
                }

                const structs = await getStructureList()
                if (Array.isArray(structs)) {
                    setStructList(structs)
                } else {
                    console.error('Нет данных:', structs)
                }

                const currency = await getCurrencyList()
                if (Array.isArray(currency)) {
                    setCurrencyList(currency)
                } else {
                    console.error('Нет данных:', currency)
                }

                setSelectedItems([])

                loadPlan()
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    useEffect(() => {
        getApprovalSatus(approvals)
    }, [approvals])

    // Второй useEffect для обновления фильтров при изменении approversAdd
    useEffect(() => {
        const approversGuids = new Set(
            approversAdd.map((approver) => approver.user_guid)
        )
        const filteredUsers = originalUserList.filter(
            (u) => !approversGuids.has(String(u.guid))
        ) // Используем оригинальный список для фильтрации
        setUserFilteredList(filteredUsers)
    }, [approversAdd, originalUserList]) // Зависимости

    const handleInputChange = (value: string) => {
        setUserFilter([value])
    }

    useEffect(() => {
        const updateFilteredList = () => {
            // Получаем список уже добавленных пользователей
            const approversGuids = new Set(
                approversAdd.map((approver) => approver.user_guid)
            )

            // Фильтруем исходный список, исключая уже выбранных
            let filtered = originalUserList.filter(
                (u) => !approversGuids.has(String(u.guid))
            )

            // Если активен текстовый фильтр, дополнительно фильтруем по тексту
            if (userFilter.length > 0) {
                filtered = filtered.filter((u) =>
                    userFilter.some(
                        (filter) =>
                            u.last_name
                                ?.toLowerCase()
                                .includes(filter.toLowerCase()) ||
                            u.first_name
                                ?.toLowerCase()
                                .includes(filter.toLowerCase()) ||
                            u.patronymic
                                ?.toLowerCase()
                                .includes(filter.toLowerCase())
                    )
                )
            }

            // Обновляем состояние отфильтрованных пользователей
            setUserFilteredList(filtered)
        }

        updateFilteredList()
    }, [approversAdd, originalUserList, userFilter]) // Зависимости

    // Функция для обработки изменения состояния отдельного checkbox в добавлении согласующих
    const handleCheckboxChange = (user_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(user_guid)) {
                return prevSelectedItems.filter(
                    (itemId) => itemId !== user_guid
                )
            } else {
                return [...prevSelectedItems, user_guid]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox для изменения ответственного
    const handleCheckboxChangeSupervisor = (user_guid: string) => {
        setSelectedSupervisor(user_guid)
    }

    const onCurrencyUpdate = async (
        planPos: PlanPosition,
        currencyCode: number | null,
        date: string
    ): Promise<Partial<PlanPosition>> => {
        const newPos: Partial<PlanPosition> = {
            currency_digital_code: currencyCode,
        }

        let currency_code: string
        let currency_name: string
        let exchange_rate: number | null
        let exchange_rate_date: string | null

        if (currencyCode === 643) {
            // ручная установка RUB
            currency_code = 'RUB'
            currency_name = 'Российский рубль'
            exchange_rate = null
            exchange_rate_date = null
        } else {
            let currencyRateByDate = await getCurrencyRateByDate(
                String(currencyCode),
                date
            )

            if (!currencyRateByDate) {
                const parseResult = await parseCurrencyCbr(date)

                if (parseResult && parseResult.message) {
                    const match = parseResult.message.match(
                        /Курсы на (\d{4}-\d{2}-\d{2})/
                    )
                    const parsedDateFromMessage = match?.[1]

                    if (
                        parsedDateFromMessage &&
                        (parseResult.message.includes('успешно') ||
                            parseResult.message.includes('уже загружены'))
                    ) {
                        currencyRateByDate = await getCurrencyRateByDate(
                            String(currencyCode),
                            parsedDateFromMessage
                        )
                    }
                }

                // Если всё равно нет данных — ставим курс 1
                if (!currencyRateByDate) {
                    currencyRateByDate = {
                        guid: '',
                        value_id: '',
                        nominal: 1,
                        currency_date: date,
                        num_code: String(currencyCode),
                        char_code: planPos.currency_code ?? '',
                        name: planPos.currency_name ?? '',
                        value: 1,
                        vunit_rate: 1,
                        char_sign: null,
                    }
                }
            }

            setCurrencyRate(currencyRateByDate)

            currency_code = currencyRateByDate.char_code
            currency_name = String(currencyRateByDate.name)
            exchange_rate = currencyRateByDate.vunit_rate
            exchange_rate_date = currencyRateByDate.currency_date
        }

        newPos.currency_code = currency_code
        newPos.currency_name = currency_name
        newPos.exchange_rate = exchange_rate
        newPos.exchange_rate_date = exchange_rate_date

        const recalculate = (
            items: { summ: number }[]
        ): { items: any[]; volume: number; volume_rub?: number } => {
            const updated = items.map((item) => ({
                ...item,
                summ_rub:
                    newPos.exchange_rate === null
                        ? undefined
                        : item.summ * Number(exchange_rate),
            }))
            const volume = updated.reduce((acc, item) => acc + item.summ, 0)
            const volume_rub =
                exchange_rate === null
                    ? undefined
                    : updated.reduce(
                          (acc, item) => acc + (item.summ_rub ?? 0),
                          0
                      )
            return { items: updated, volume, volume_rub }
        }

        if (planPos.long_term_volumes) {
            const { items, volume, volume_rub } = recalculate(
                planPos.long_term_volumes.details?.items ?? []
            )
            newPos.long_term_volumes = {
                ...planPos.long_term_volumes,
                details: { items },
                volume,
                volume_rub,
                currency: {
                    code: currency_code,
                    name: currency_name,
                    digital_code: currencyCode,
                },
                exchange_rate,
                exchange_rate_date,
            }
        }

        if (planPos.long_term_smb_volumes) {
            const { items, volume, volume_rub } = recalculate(
                planPos.long_term_smb_volumes.details?.items ?? []
            )
            newPos.long_term_smb_volumes = {
                ...planPos.long_term_smb_volumes,
                details: { items },
                volume,
                volume_rub,
                currency: {
                    code: currency_code,
                    name: currency_name,
                    digital_code: currencyCode,
                },
                exchange_rate,
                exchange_rate_date,
            }
        }

        return newPos
    }

    const handleSave = () => {
        if (pos.currency_code == 'RUB') {
            pos.maximum_contract_price_rub = null
        } else if (pos.exchange_rate !== null) {
            const rub = Number(pos.maximum_contract_price) * pos.exchange_rate
            pos.maximum_contract_price_rub = Math.round(rub * 100) / 100
        }

        if (pos.long_term_volumes !== null) {
            pos.long_term_volumes.currency.code = pos.currency_code
            pos.long_term_volumes.currency.name = pos.currency_name
            pos.long_term_volumes.currency.digital_code =
                pos.currency_digital_code
            pos.long_term_volumes.exchange_rate_date = pos.exchange_rate_date
        }

        if (pos.long_term_smb_volumes !== null) {
            pos.long_term_smb_volumes.currency.code = pos.currency_code
            pos.long_term_smb_volumes.currency.name = pos.currency_name
            pos.long_term_smb_volumes.currency.digital_code =
                pos.currency_digital_code
            pos.long_term_smb_volumes.exchange_rate_date =
                pos.exchange_rate_date
        }

        const url = window.location.href
        const mode_url =
            url.includes('/draft/') || url.includes('/new')
                ? `${process.env.REACT_APP_API_URL}/api/backend/v2/plan_position_draft/?reg_number=${encodeURIComponent(reg_number as string)}&guid=${encodeURIComponent(pos.guid)}`
                : `${process.env.REACT_APP_API_URL}/api/backend/v2/plan_positions/${encodeURIComponent(pos.guid)}?reg_number=${encodeURIComponent(reg_number as string)}`

        fetch(mode_url, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(pos),
        })
            .then((response) => {
                if (response.ok) {
                    // success
                    return response.json()
                } else {
                    throw new Error('Неверный почтовый адрес или пароль')
                }
            })
            .catch((error) => undefined)
            .finally(() => undefined)
    }

    let purchasePlannedDate: Date
    if (pos.purchase_planned_date !== null) {
        purchasePlannedDate = new Date(pos.purchase_planned_date)
    } else if (
        pos.purchase_period_year !== null &&
        pos.purchase_period_month !== null
    ) {
        purchasePlannedDate = new Date(
            pos.purchase_period_year,
            pos.purchase_period_month - 1
        )
    } else {
        purchasePlannedDate = new Date()
    }

    let contractEndDate: Date
    if (pos.contract_end_date !== null) {
        contractEndDate = new Date(pos.contract_end_date)
    } else {
        contractEndDate = new Date(getCurrentYear(), getCurrentMonth())
    }

    useEffect(() => {
        if (pos.purchase_planned_date !== null) {
            setPos((prev) => ({
                ...prev,
                purchase_planned_date: `${purchasePlannedDate.getFullYear()}-${String(purchasePlannedDate.getMonth() + 1).padStart(2, '0')}-${String(purchasePlannedDate.getDate()).padStart(2, '0')}`,
            }))
        } else {
            setPos((prev) => ({
                ...prev,
                purchase_period_year: purchasePlannedDate.getFullYear(),
                purchase_period_month: purchasePlannedDate.getMonth() + 1,
            }))
        }
        setPos((prev) => ({
            ...prev,
            contract_end_date: `${contractEndDate.getFullYear()}-${String(contractEndDate.getMonth() + 1).padStart(2, '0')}-${String(contractEndDate.getDate()).padStart(2, '0')}`,
        }))

        setPos((prev) => ({
            ...prev,
            purchase_period_quarter: getQuarterByMonth(
                purchasePlannedDate.getMonth() + 1
            ),
        }))

        if (isNew) {
            const fetchData = async () => {
                const structs = await getStructureList()
                if (structs) {
                    const matchingStruct = structs.find(
                        (struct) =>
                            struct.guid.toLowerCase() === user?.object.struct_id
                    )

                    const structShortName =
                        matchingStruct?.struct_short_name || ''
                    setUserStructName(structShortName)
                    const structGuid = matchingStruct?.guid || ''
                    setUserStructGuid(structGuid)

                    setPos((prev) => ({
                        ...prev,
                        struct_guid: prev.struct_guid ?? structGuid,
                        struct_name: prev.struct_name ?? structShortName,
                    }))
                }
            }
            fetchData()
        }
    }, [])

    const handleCurrencyExchangeRateChange = (newExchangeRate: number | 0) => {
        setPos((prev) => {
            const isRub = newExchangeRate <= 0
            const rate = isRub ? 1 : newExchangeRate
            const digitalCode = isRub ? 643 : prev.currency_digital_code
            const currencyCode = isRub ? 'RUB' : prev.currency_code
            const currencyName = isRub ? 'Российский рубль' : prev.currency_name
            const exchangeRate = isRub ? null : rate
            const exchangeRateDate = isRub ? null : prev.exchange_rate_date

            const convertItems = (items: ItemDetail[]) =>
                items.map((item) => ({
                    ...item,
                    summ_rub: isRub
                        ? undefined
                        : Math.round(item.summ * rate * 100) / 100,
                }))

            const updateLongTermVolumes = (
                volume: LongTermVolumes | null
            ): LongTermVolumes | null => {
                if (!volume) return null

                const newItems = convertItems(volume.details?.items ?? [])
                const newVolumeRub = isRub
                    ? undefined
                    : Math.round(volume.volume * rate * 100) / 100

                return {
                    ...volume,
                    details: { items: newItems },
                    volume_rub: newVolumeRub,
                    exchange_rate: exchangeRate,
                    exchange_rate_date: exchangeRateDate,
                    currency: {
                        code: currencyCode,
                        name: currencyName,
                        digital_code: digitalCode,
                    },
                }
            }

            const maximum_contract_price_rub = isRub
                ? null
                : Math.round((prev.maximum_contract_price ?? 0) * rate * 100) /
                  100

            return {
                ...prev,
                currency_digital_code: digitalCode,
                currency_code: currencyCode,
                currency_name: currencyName,
                exchange_rate: exchangeRate,
                exchange_rate_date: exchangeRateDate,
                maximum_contract_price_rub,
                long_term_volumes: updateLongTermVolumes(
                    prev.long_term_volumes
                ),
                long_term_smb_volumes: updateLongTermVolumes(
                    prev.long_term_smb_volumes
                ),
            }
        })

        setHaveError(true)
    }

    const handleCurrencyExchangeDateChange = async (
        newExchangeRateDate: Date,
        isRetry: boolean
    ): Promise<void> => {
        const setInputDate = newExchangeRateDate.toISOString().slice(0, 10)
        let targetDate = newExchangeRateDate.toISOString().slice(0, 10)
        const currencyCode = pos.currency_digital_code

        let parsedDateFromMessage: string | undefined

        if (currencyCode === 643) {
            setPos((prev) => ({
                ...prev,
                exchange_rate_date: targetDate,
                long_term_volumes: prev.long_term_volumes
                    ? {
                          ...prev.long_term_volumes,
                          exchange_rate_date: targetDate,
                      }
                    : null,
                long_term_smb_volumes: prev.long_term_smb_volumes
                    ? {
                          ...prev.long_term_smb_volumes,
                          exchange_rate_date: targetDate,
                      }
                    : null,
            }))
            setHaveError(true)
            return
        }

        let currencyRateData = await getCurrencyRateByDate(
            String(currencyCode),
            targetDate
        )

        if (!currencyRateData) {
            const parseResult = await parseCurrencyCbr(targetDate)

            const match = parseResult?.message?.match(
                /Курсы на (\d{4}-\d{2}-\d{2})/
            )
            parsedDateFromMessage = match?.[1]

            // 🔁 Если мы еще не пробовали retry и есть дата — повторим
            if (parsedDateFromMessage && !isRetry) {
                const retryRate = await getCurrencyRateByDate(
                    String(currencyCode),
                    parsedDateFromMessage
                )

                if (retryRate) {
                    // обновим дату и данные
                    newExchangeRateDate = new Date(parsedDateFromMessage)
                    currencyRateData = retryRate
                    targetDate = parsedDateFromMessage
                } else {
                    // ❌ Не удалось повторно получить — ставим курс = 1
                    currencyRateData = {
                        guid: '',
                        value_id: '',
                        nominal: 1,
                        currency_date: parsedDateFromMessage,
                        num_code: String(currencyCode),
                        char_code: pos.currency_code ?? '',
                        name: pos.currency_name ?? '',
                        value: 1,
                        vunit_rate: 1,
                        char_sign: null,
                    }
                }
            }

            // Если это уже retry или нет даты из парсера, ставим курс = 1
            if (!currencyRateData) {
                currencyRateData = {
                    guid: '',
                    value_id: '',
                    nominal: 1,
                    currency_date: targetDate,
                    num_code: String(currencyCode),
                    char_code: pos.currency_code ?? '',
                    name: pos.currency_name ?? '',
                    value: 1,
                    vunit_rate: 1,
                    char_sign: null,
                }
            }
        }

        const updated = {
            currency_digital_code: currencyCode,
            currency_code: currencyRateData.char_code,
            currency_name: String(currencyRateData.name),
            exchange_rate: currencyRateData.vunit_rate,
            exchange_rate_date: setInputDate,
        }

        setPos((prev) => {
            const updateBlock = (block: any) => {
                if (!block) return null
                const newItems = block.details.items.map((item: Item) => ({
                    ...item,
                    summ_rub:
                        updated.exchange_rate == null
                            ? undefined
                            : item.summ * updated.exchange_rate,
                }))
                const volume = newItems.reduce(
                    (acc: number, item: Item) => acc + item.summ,
                    0
                )
                const volume_rub = newItems.reduce(
                    (acc: number, item: Item) => acc + (item.summ_rub ?? 0),
                    0
                )
                return {
                    ...block,
                    details: { items: newItems },
                    volume,
                    volume_rub,
                    currency: {
                        code: updated.currency_code,
                        name: updated.currency_name,
                        digital_code: currencyCode,
                    },
                    exchange_rate: updated.exchange_rate,
                    exchange_rate_date: updated.exchange_rate_date,
                }
            }

            return {
                ...prev,
                ...updated,
                long_term_volumes: updateBlock(prev.long_term_volumes),
                long_term_smb_volumes: updateBlock(prev.long_term_smb_volumes),
            }
        })

        setCurrencyRate(currencyRateData)
        setHaveError(true)
    }

    // Обработчик изменения наличия бюджетных средств
    const handleBudgetFundsChange = (value: boolean) => {
        setPos((prev) => ({
            ...prev,
            maximum_contract_price_from_budget: value,
        }))
        if (value == false) {
            setPos((prev) => ({
                ...prev,
                position_financial_provision_volumes: null,
            }))
        }
        setHaveError(true)
    }

    // Обработчик изменения данных в таблице
    const handlePositionFinancialProvisionChange = (
        updatedItems: Items[] | null
    ) => {
        setPos((prev) => ({
            ...prev,
            position_financial_provision_volumes: {
                ...prev.position_financial_provision_volumes,
                items: updatedItems,
            },
        }))
        setHaveError(true)
    }

    const handleApprovalPopup = () => {
        setApprovalCreatePopup(!approvalCreatePopup)
    }

    const saveApprovalResult = async (
        approverGuid: string,
        status: number,
        comments: string
    ) => {
        try {
            setSaveLoading(true)
            const currentDate = new Date().toISOString().split('T')[0] // Формат YYYY-MM-DD

            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/approvers/${approverGuid}`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        approve_date: currentDate,
                        status: status,
                        comment: comments,
                    }),
                }
            )

            if (!response.ok) {
                throw new Error('Ошибка сохранения результата согласования')
            }

            // Обновляем данные согласующего в массиве approvers
            setApprovers((prevApprovers) =>
                prevApprovers.map((approver) =>
                    approver.guid === approverGuid
                        ? {
                              ...approver,
                              approve_date: currentDate,
                              status: status,
                              comment: comments,
                          }
                        : approver
                )
            )

            // Заново загружаем approvals
            const updatedApprovals = await getApprovals(pos.guid)
            setApprovals(updatedApprovals)

            // Заново загружаем approvers для текущего approval
            const currentApprovalGuid = approvals.find(
                (approval) =>
                    approval.guid ===
                    approvers.find((a) => a.guid === approverGuid)
                        ?.approval_guid
            )?.guid

            if (currentApprovalGuid) {
                const updatedApprovers = await getApprovers(currentApprovalGuid)
                setApprovers(updatedApprovers)

                // Обновляем userApprovers
                const excludedApprover = updatedApprovers.filter(
                    (approver) => approver.user_guid === user?.object.user_id
                )
                setUserApprovers(excludedApprover)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setSaveLoading(false)
        }
    }

    useEffect(() => {
        const fetchApprovals = async () => {
            const data = await getApprovals(pos.guid)
            setApprovals(data)

            const initialIndex = data.findIndex(
                (approval) => approval.status === 0
            )
            if (initialIndex !== -1) {
                const initialApprovalGuid = data[initialIndex].guid
                setExpandedIndex(initialIndex)
                try {
                    setLoading(true)
                    const approversData =
                        await getApprovers(initialApprovalGuid)

                    // Check if is_saw is false for the user-specific approvers
                    for (const approver of approversData) {
                        if (approver.user_guid === user?.object.user_id) {
                            if (
                                approver.is_saw === false ||
                                approver.is_saw === null
                            ) {
                                // Run updateApproverSatatus if is_saw is false
                                await updateApproverSatatus(
                                    initialApprovalGuid,
                                    approver.guid
                                )
                            }
                        }
                    }

                    // Re-fetch approvers data after update
                    const updatedApprovers =
                        await getApprovers(initialApprovalGuid)
                    setApprovers(updatedApprovers)

                    // Filter user-specific approvers
                    const excludedApprover = approversData.filter(
                        (approver) =>
                            approver.user_guid === user?.object.user_id
                    )
                    setUserApprovers(excludedApprover)
                } catch (error) {
                    console.error(error)
                } finally {
                    setLoading(false)
                }
            }
        }
        fetchApprovals()
    }, [])

    const toggleAccordion = async (index: number, approvalGuid: string) => {
        if (expandedIndex === index) {
            setExpandedIndex(null)
            return
        }

        try {
            setLoading(true)
            const data = await getApprovers(approvalGuid)

            // Store all approvers as is
            setApprovers(data)

            // Filter user-specific approvers
            const excludedApprover = data.filter(
                (approver) => approver.user_guid === user?.object.user_id
            )
            setUserApprovers(excludedApprover)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }

        setExpandedIndex(index)
    }

    const createApprovers = async (
        approver: ApproversAdd,
        approvalGuid: string
    ) => {
        setApprovalCreate(true) // Включаем анимацию загрузки
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + `/api/backend/v1/approvers`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ...approver,
                        approval_guid: approvalGuid, // Добавляем guid созданного Approval
                    }),
                }
            )
            if (!response.ok) {
                throw new Error('Ошибка при добавлении согласующего')
            }
            console.log('Согласующий добавлен')
        } catch (error) {
            console.error('Ошибка при добавлении согласующего:', error)
        } finally {
            setApprovalCreate(false) // Отключаем анимацию загрузки
        }
    }

    const deleteApprovers = async (approver_guid: string) => {
        setApprovalCreate(true) // Включаем анимацию загрузки
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/approvers/${approver_guid}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            if (!response.ok) {
                throw new Error('Ошибка при удалении согласующего')
            }

            // Заново загружаем approvers для созданного approval
            const updatedApprovers = await getApprovers(approvalChangeGuid)
            setApprovers(updatedApprovers) // Обновляем список согласующих

            console.log('Согласующий удален')
        } catch (error) {
            console.error('Ошибка при удалении согласующего:', error)
        } finally {
            setApprovalCreate(false) // Отключаем анимацию загрузки
        }
    }

    const getApprovalsForChange = async (
        approvalGuid: string
    ): Promise<Approvals> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/approvals/${approvalGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса по согласующим')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const updateApprovers = async (
        approver: ApproversAdd,
        approvalGuid: string
    ) => {
        setApprovalCreate(true) // Включаем анимацию загрузки
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/approvers/${approver.guid}`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ...approver,
                        approval_guid: approvalGuid, // Добавляем guid созданного Approval
                    }),
                }
            )
            if (!response.ok) {
                throw new Error('Ошибка при добавлении согласующего')
            }
            console.log('Согласующий добавлен')
        } catch (error) {
            console.error('Ошибка при добавлении согласующего:', error)
        } finally {
            setApprovalCreate(false) // Отключаем анимацию загрузки
        }
    }

    const updateApproval = async (approvalGuid: string) => {
        setApprovalChanging(true) // Включаем анимацию загрузки
        try {
            // Шаг 2. Получаем список существующих согласующих для данного approval
            const existingApprovers = await getApprovers(approvalGuid)
            const existingApproverGuids = new Set(
                existingApprovers.map((item) => item.user_guid)
            )

            // Шаг 3. Добавляем только новых согласующих
            for (const approver of approversAdd) {
                if (!existingApproverGuids.has(approver.user_guid)) {
                    await createApprovers(approver, approvalGuid)
                }
            }
            setExpandedIndex(null)

            // Шаг 1. Обновляем данные approval
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/approvals/${approvalGuid}`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        start_date: approvalStartDate,
                        end_date: approvalEndDate,
                        priority: approvalPriority,
                        comment: approvalComment,
                    }),
                }
            )

            if (!response.ok) {
                throw new Error('Ошибка при обновлении согласования')
            }

            console.log('Данные согласования успешно обновлены')

            // Шаг 4. Заново загружаем approvals
            const updatedApprovals = await getApprovals(pos.guid)
            setApprovals(updatedApprovals)

            // Шаг 5. Заново загружаем approvers для обновленного approval
            const updatedApprovers = await getApprovers(approvalGuid)
            setApprovers(updatedApprovers)

            setApprovalCreatePopup(false)

            console.log('Согласующие успешно обновлены или добавлены')
        } catch (error) {
            console.error(
                'Ошибка при обновлении согласования или согласующих:',
                error
            )
        } finally {
            setApprovalChanging(false) // Отключаем анимацию загрузки
        }
    }

    useEffect(() => {
        const updateDataForChange = async () => {
            if (approvalChangeGuid) {
                try {
                    const updatedApproval =
                        await getApprovalsForChange(approvalChangeGuid)

                    console.log(
                        'Результат getApprovalsForChange:',
                        updatedApproval
                    )

                    if (
                        updatedApproval &&
                        typeof updatedApproval === 'object' &&
                        !Array.isArray(updatedApproval)
                    ) {
                        // Обрабатываем как объект
                        setApprovalStartDate(updatedApproval.start_date)
                        setApprovalEndDate(updatedApproval.end_date)
                        setApprovalPriority(updatedApproval.priority)
                        setApprovalComment(updatedApproval.comment || '')
                    } else {
                        console.error(
                            'Ошибка: Неподдерживаемый формат ответа',
                            updatedApproval
                        )
                    }

                    const updatedApprovers =
                        await getApprovers(approvalChangeGuid)
                    setApproversAdd(updatedApprovers)
                } catch (error) {
                    console.error('Ошибка при обновлении данных:', error)
                }
            }
        }
        updateDataForChange()
    }, [approvalChangeGuid])

    useEffect(() => {
        const updateDataForAdd = async () => {
            if (approvalAddGuid) {
                try {
                    const updatedApproval =
                        await getApprovalsForChange(approvalAddGuid)

                    console.log(
                        'Результат getApprovalsForAdd:',
                        updatedApproval
                    )

                    if (
                        updatedApproval &&
                        typeof updatedApproval === 'object' &&
                        !Array.isArray(updatedApproval)
                    ) {
                        // Обрабатываем как объект
                        setApprovalStartDate(updatedApproval.start_date)
                        setApprovalEndDate(updatedApproval.end_date)
                        setApprovalPriority(updatedApproval.priority)
                        setApprovalComment(updatedApproval.comment || '')
                    } else {
                        console.error(
                            'Ошибка: Неподдерживаемый формат ответа',
                            updatedApproval
                        )
                    }

                    const updatedApprovers = await getApprovers(approvalAddGuid)
                    setApproversAdd(updatedApprovers)
                } catch (error) {
                    console.error('Ошибка при обновлении данных:', error)
                }
            }
        }
        updateDataForAdd()
    }, [approvalAddGuid])

    const createApproval = async () => {
        setApprovalCreate(true) // Включаем анимацию загрузки
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + `/api/backend/v1/approvals`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        start_date: approvalStartDate,
                        end_date: approvalEndDate,
                        priority: approvalPriority,
                        comment: approvalComment,
                        status: 0,
                        plan_pos_guid: pos.guid,
                        create_date: getCurrentDateTime(),
                    }),
                }
            )
            if (!response.ok) {
                throw new Error('Ошибка при создании согласования')
            }

            setExpandedIndex(null)

            // Получаем данные созданного approval
            const createdApproval = await response.json()

            // Проходим по массиву approversAdd и создаём каждого согласующего
            for (const approver of approversAdd) {
                await createApprovers(approver, createdApproval.guid)
            }

            // Заново загружаем approvals
            const updatedApprovals = await getApprovals(pos.guid)
            setApprovals(updatedApprovals)

            // Заново загружаем approvers для созданного approval
            const updatedApprovers = await getApprovers(createdApproval.guid)
            setApprovers((prev) => [...prev, ...updatedApprovers]) // Обновляем список согласующих

            setApprovalCreatePopup(false)
            console.log('Согласование создано')
        } catch (error) {
            console.error('Ошибка при создании согласования:', error)
        } finally {
            setApprovalCreate(false) // Отключаем анимацию загрузки
        }
    }

    const handleApprovalCreate = () => {
        setCreating(true)
        createApproval()
        setCreating(false)
    }

    const handleApprovalDeletePopup = (approvalGuid: string) => {
        if (approvalGuid == '') {
            setApprovalDeletePopup(!approvalDeletePopup)
        } else {
            setApprovalDeleteGuid(approvalGuid)
            setApprovalDeletePopup(!approvalDeletePopup)
        }
    }

    const deleteApproval = async (approval_guid: string): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/approvals/${approval_guid}`

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to delete approval. Status: ${response.status}`
                )
            }

            setExpandedIndex(null)

            // Заново загружаем approvals
            const updatedApprovals = await getApprovals(pos.guid)
            setApprovals(updatedApprovals)

            // Заново загружаем approvers для созданного approval
            const updatedApprovers = await getApprovers(approval_guid)
            setApprovers(updatedApprovers) // Обновляем список согласующих

            console.log('Approval deleted successfully')
            setApprovalDeleteGuid('')
            setApprovalDeletePopup(!approvalDeletePopup)
        } catch (error) {
            console.error('Error while deleting approval:', error)
            setDeleting(false)
        } finally {
        }
    }

    const handleApprovalDelete = () => {
        setDeleting(true)
        deleteApproval(approvalDeleteGuid)
        setDeleting(false)
    }

    const handleAddApproversPopup = () => {
        setAddApproversPopup(!addApproversPopup)
        setSelectedItems([])
        setUserFilter([''])
    }

    const handleRemoveApprover = (user_guid: string) => {
        setApproversAdd((prev) =>
            prev.filter((approver) => approver.user_guid !== user_guid)
        )
    }

    const handlePreAddApproval = () => {
        const currentDate = getCurrentDate() // Получаем текущую дату один раз
        const newApprovers = selectedItems.map((u, index) => ({
            guid: null,
            approve_date: null, // Устанавливаем null
            status: 0, // Статус по умолчанию
            comment: null, // Комментарий по умолчанию
            user_position: approversAdd.length + index + 1, // Позиция: текущая длина массива + индекс + 1
            user_guid: String(u), // Уникальный идентификатор пользователя
            approval_guid: null, // По умолчанию null
            organization_guid: null,
            add_approver_date: currentDate, // Устанавливаем текущую дату
            add_user_guid: null,
            is_saw: false,
        }))

        // Обновляем массив approversAdd
        setApproversAdd((prev) => [...prev, ...newApprovers])

        // Очищаем выбранные элементы и закрываем popup
        setSelectedItems([])
        setAddApproversPopup(false)
        setUserFilter([''])
    }

    // const handleChangeApprovalPopup = async () => {}

    const handleApprovalChange = () => {
        setApprovalChanging(true)
        updateApproval(approvalChangeGuid)
        setApprovalChanging(false)
    }

    const handleApprovalAdd = () => {
        setApprovalChanging(true)
        updateApproval(approvalAddGuid)
        setApprovalChanging(false)
    }

    const handleErrorPopup = () => {
        setErrorPopup(!errorPopup)
    }

    const handleChangeSupervisorPopup = () => {
        setChangeSupervisorPopup(!changeSupervisorPopup)
        setSelectedSupervisor(pos.supervisor_user_guid)
        setUserFilter([''])
    }

    const handleSupervisorChange = () => {
        // Обновляем ответственного
        if (selectedSupervisor != '') {
            setPos((prev) => ({
                ...prev,
                supervisor_user_guid: selectedSupervisor,
            }))
            setSupervisorChanged(true) // Устанавливаем флаг изменения ответственного
        }

        // Очищаем выбранные элементы и закрываем popup
        setSelectedSupervisor('')
        setChangeSupervisorPopup(false)
        setUserFilter([''])
    }

    useEffect(() => {
        // Вызываем если supervisor_user_guid изменился и нажата кнопка изменить
        if (supervisorChanged) {
            handleSave()
            setSupervisorChanged(false) // Сбрасываем флаг
        }
    }, [pos.supervisor_user_guid])

    // Функция валидации, которая собирает ошибки для всех компонентов
    const validateForm = (): boolean => {
        const plannedYear = getStartDate(pos).getFullYear()
        const reportingYear = plan[0]?.reporting_year
        const skipValidation =
            (reportingYear &&
                plannedYear - reportingYear >= 1 &&
                pos.is_purchase_smb === true) ||
            pos.planned_after_second_year === true

        const purchaseContractErrors: PurchaseContractErrors = {}
        const purchaseMethodErrors: PurchaseMethodErrors = {}
        const purchaseChangeInfoErrors: PurchaseChangeInfoErrors = {}
        const purchaseBudgetFundsErrors: PurchaseBudgetFundsErrors = {}
        const purchasePaymentBreakdownErrors: PurchasePaymentBreakdownErrors =
            {}
        const purchaseItemsSectionErrors: PurchaseItemsSectionErrors = {}
        const purchaseDatesInfoErrors: PurchaseDatesInfoErrors = {}

        // Проверяем информацию об изменении, если это изменение
        if (pos.status === 'C') {
            if (
                !pos.changed_GWS_and_dates &&
                !pos.changed_NMSK_more_ten_percent &&
                !pos.other_changes
            ) {
                purchaseChangeInfoErrors.changeReason =
                    'Информация об изменении обязательно'
            }
            if (pos.other_changes) {
                if (
                    !pos.modification_description ||
                    pos.modification_description.trim() === ''
                ) {
                    purchaseChangeInfoErrors.modification_description =
                        '"Обоснование внесения изменений" - Поле обязательно к заполнению'
                }
            }
        }

        // Проверяем поле contract_subject (наименование предмета договора)
        if (!pos.contract_subject || pos.contract_subject.trim() === '') {
            purchaseContractErrors.contract_subject =
                '"Наименование предмета договора" - поле обязательно к заполнению'
        }

        // Проверяем поле minimum_requirements (минимально необходимые требования)
        if (
            !pos.minimum_requirements ||
            pos.minimum_requirements.trim() === ''
        ) {
            purchaseContractErrors.minimum_requirements =
                '"Минимально необходимые требования, предъявляемые к закупаемым товарам, работам, услугам" - поле обязательно к заполнению'
        }

        // Проверяем поле order_pricing (обоснование начальной (максимальной) цены договора)
        if (
            (!pos.order_pricing || pos.order_pricing.trim() === '') &&
            !(
                (pos.is_purchase_smb === true &&
                    getStartDate(pos).getFullYear() - plan[0]?.reporting_year >=
                        1) ||
                pos.planned_after_second_year === true
            )
        ) {
            purchaseContractErrors.order_pricing =
                '"Обоснование начальной (максимальной) цены договора" - поле обязательно к заполнению'
        }

        // Проверка информации о финансировании за счет бюджетных средств
        if (pos.maximum_contract_price_from_budget) {
            const funds = pos.position_financial_provision_volumes
            if (!funds || !funds.items || funds.items.length === 0) {
                purchaseBudgetFundsErrors.position_financial_provision_volumes =
                    '"Наличие бюджетных средств" - таблица обязательна к заполнению. Добавьте строку в таблицу'
            } else {
                // Для каждого элемента массива собираем ошибки
                let sum_financial_volume_position_rub = 0
                const itemsErrors: PurchaseBudgetFundsItemErrors[] =
                    funds.items.map((item) => {
                        const itemErrors: PurchaseBudgetFundsItemErrors = {}

                        sum_financial_volume_position_rub =
                            sum_financial_volume_position_rub +
                            item.financial_volume_position_rub

                        if (
                            !item.target_code ||
                            item.target_code.trim() === ''
                        ) {
                            itemErrors.target_code =
                                '"Код ЦСР" - поле обязательно к заполнению'
                        }
                        if (
                            !item.target_name ||
                            item.target_name.trim() === ''
                        ) {
                            itemErrors.target_name =
                                '"Наименование федерального проекта" - поле обязательно к заполнению'
                        }
                        if (!item.type_code || item.type_code.trim() === '') {
                            itemErrors.type_code =
                                '"Код вида расходов (КВР)" - поле обязательно к заполнению'
                        }
                        if (!item.type_name || item.type_name.trim() === '') {
                            itemErrors.type_name =
                                '"Наименование вида расходов" - поле обязательно к заполнению'
                        }
                        if (
                            item.financial_volume_position_rub === null ||
                            item.financial_volume_position_rub === undefined ||
                            item.financial_volume_position_rub <= 0
                        ) {
                            itemErrors.financial_volume_position_rub =
                                '"Объем финансового обеспечения" - поле обязательно к заполнению'
                        }

                        return itemErrors
                    })

                if (pos.currency_digital_code == 643) {
                    if (
                        sum_financial_volume_position_rub >
                        (pos.maximum_contract_price
                            ? pos.maximum_contract_price
                            : 0)
                    ) {
                        purchaseBudgetFundsErrors.sum_financial_volume_position_rub =
                            '"Наличие бюджетных средств" - объем бюджетных средств не может быть больше НМЦД'
                        purchaseContractErrors.maximum_contarct_price =
                            '"Наличие бюджетных средств" - объем бюджетных средств не может быть больше НМЦД'
                    }
                } else {
                    if (pos.maximum_contract_price_rub !== null) {
                        if (
                            sum_financial_volume_position_rub >
                            pos.maximum_contract_price_rub
                        ) {
                            purchaseBudgetFundsErrors.sum_financial_volume_position_rub =
                                '"Наличие бюджетных средств" - объем бюджетных средств не может быть больше НМЦД'
                            purchaseContractErrors.maximum_contarct_price_rub =
                                '"Наличие бюджетных средств" - объем бюджетных средств не может быть больше НМЦД'
                        }
                    }
                }

                // Если хотя бы в одном элементе есть ошибка, передаем массив ошибок
                if (itemsErrors.some((err) => Object.keys(err).length > 0)) {
                    purchaseBudgetFundsErrors.items = itemsErrors
                }
            }
        }

        // Проверка срока исполнения договора
        if (!skipValidation) {
            if (!pos.contract_end_date) {
                purchaseDatesInfoErrors.contract_end_date =
                    '"Срок исполнения договора" - поле обязательно к заполнению'
            }
        }

        // Проверка на срок размещения и исполнения
        if (!pos.purchase_planned_date) {
            // Формируем дату покупки в формате "YYYY-MM-DD"
            const purchaseDateString = `${pos.purchase_period_year}-${pos.purchase_period_month?.toString().padStart(2, '0')}-01`
            // Преобразуем строки в объекты Date
            const purchaseDate = new Date(purchaseDateString)
            const contractDate = pos.contract_end_date
                ? new Date(pos.contract_end_date)
                : new Date(getCurrentYear(), getCurrentMonth(), 1)

            if (contractDate < purchaseDate) {
                purchaseDatesInfoErrors.purchase_planned_date =
                    '"Срок исполнения договора" - срок исполнения договора не может быть раньше даты размещения извещения о закупке'
            }
        } else {
            const purchaseDate = new Date(
                pos.purchase_planned_date.substring(0, 7) + '-01'
            )
            const contractDate = pos.contract_end_date
                ? new Date(pos.contract_end_date)
                : new Date(getCurrentYear(), getCurrentMonth(), 1)

            if (contractDate < purchaseDate) {
                purchaseDatesInfoErrors.purchase_planned_date =
                    '"Срок исполнения договора" - срок исполнения договора не может быть раньше даты размещения извещения о закупке'
            }
        }

        // Проверка LongTerm
        if (pos.long_term == true) {
            // Если разница меньше 1 года или закупка не МСП, выполняем проверки
            if (!skipValidation) {
                const funds = pos.long_term_volumes
                const fundsSMB = pos.long_term_smb_volumes
                if (!funds || !fundsSMB) {
                    purchasePaymentBreakdownErrors.longTermVolue =
                        '"Информация об объемах оплаты долгосрочного договора" - поле обязательно к заполнению'
                } else {
                    // Для каждого элемента массива собираем ошибки
                    if (pos.maximum_contract_price != funds.volume) {
                        purchasePaymentBreakdownErrors.longTermVolueRub =
                            '"Информация об объемах оплаты долгосрочного договора" - объем оплаты долгосрочного договора за все года исполнения долгосрочного договора должен быть равен НМЦД'
                        purchaseContractErrors.maximum_contarct_price =
                            '"Сведения о НМЦД" - НМЦД должен быть равен объему оплаты долгосрочного договора за все года исполнения долгосрочного договора'
                    }

                    if (
                        (pos.maximum_contract_price
                            ? pos.maximum_contract_price
                            : 0) < fundsSMB.volume
                    ) {
                        purchasePaymentBreakdownErrors.longTermVolueSMBRub =
                            '"Информация об объемах привлечения субъектов МСП" - объем привлечения субъектов МСП не может быть больше объема оплаты долгосрочного договора'
                        purchaseContractErrors.maximum_contarct_price =
                            '"Сведения о НМЦД" - объем привлечения субъектов МСП не может быть больше объема оплаты долгосрочного договора и НМЦД'
                    }
                }
                console.log(purchasePaymentBreakdownErrors.longTermVolue)
            }
        }

        if (!skipValidation) {
            // Если роль admin или specialist, то проверяем способ закупки
            if (isUserRoleName === 'admin' || isUserRoleName === 'specialist') {
                if (
                    !pos.purchase_method_name ||
                    pos.purchase_method_name.trim() === ''
                ) {
                    purchaseMethodErrors.purchase_method_name =
                        '"Способ закупки" - поле обязательно к заполнению'
                }

                if (pos.is_purchase_ignored) {
                    if (!pos.purchase_category) {
                        purchaseMethodErrors.purchase_category =
                            '"Категория закупки" - поле обязательно к заполнению'
                    }
                }
            }
        }

        // Проверка информации о закупаемых товарах, работах, услугах
        if (pos.is_general_address == true) {
            if (!pos.okato || pos.okato.trim() === '') {
                purchaseItemsSectionErrors.okato =
                    '"Регион" - поле обязательно к заполнению'
            }
        }

        // Проверка даты курса валют
        if (pos.exchange_rate_date != null) {
            const todayDate = getCurrentDate()
            if (pos.exchange_rate_date > todayDate) {
                purchaseContractErrors.exchange_rate_date =
                    '"Дата курса валюты" - не может быть больше текущей даты'
            }
        }

        // -------------------------------
        // Проверка строк таблицы (PurchaseItemsTable)
        // Предполагается, что purchaseItemsRows - это массив строк, полученных из PurchaseItemsTable
        if (!purchaseItemsRows || purchaseItemsRows.length === 0) {
            purchaseItemsSectionErrors.rowsEmpty =
                '"Информация о закупаемых товарах, работах, услугах" - необходимо ввести информацию о товаре, работе, услуге. Добавьте строку в таблицу'
        } else {
            const rowsErrors: PurchaseItemRowErrors[] = purchaseItemsRows.map(
                (row) => {
                    const rowErrors: PurchaseItemRowErrors = {}

                    // Пример проверки для строки: поле additional_info обязательно
                    if (
                        !row.additional_info ||
                        row.additional_info.trim() === ''
                    ) {
                        rowErrors.additional_info =
                            '"Наименование товара, работы, услуги" - Поле обязательно к заполнению'
                    }

                    if (!skipValidation) {
                        // Проверка количества qty и единицы измерения okei_code только, если impossible_to_determine_attr не true
                        if (!row.impossible_to_determine_attr) {
                            // Проверка количества (qty должно быть положительным числом)
                            if (!row.qty || row.qty <= 0) {
                                rowErrors.qty =
                                    '"Количество (объем)" - количество должно быть больше 0'
                            }

                            // Пример проверки для строки: поле okei_code обязательно
                            if (!row.okei_code || row.okei_code.trim() === '') {
                                rowErrors.okei_code =
                                    '"Единица измерения" - поле обязательно к заполнению'
                            }
                        }
                    }

                    if (!(pos.planned_after_second_year === true)) {
                        // Пример проверки для строки: поле type_object_purchase обязательно
                        if (
                            !row.type_object_purchase ||
                            row.type_object_purchase.trim() === ''
                        ) {
                            rowErrors.type_object_purchase =
                                '"Тип объекта закупки" - поле обязательно к заполнению'
                        }
                    }

                    // Пример проверки для строки: поле okei_code обязательно
                    if (!row.okpd2_code || row.okpd2_code.trim() === '') {
                        rowErrors.okpd2_code =
                            '"ОКПД2" - поле обязательно к заполнению'
                    }

                    // Пример проверки для строки: поле okei_code обязательно
                    if (!row.okved2_code || row.okved2_code.trim() === '') {
                        rowErrors.okved2_code =
                            '"ОКВЭД2" - поле обязательно к заполнению'
                    }

                    // Проверка информации о закупаемых товарах, работах, услугах
                    if (pos.is_general_address == false) {
                        if (!row.okato || row.okato.trim() === '') {
                            rowErrors.okato =
                                '"Регион" - поле обязательно к заполнению'
                        }
                    }

                    // Можно добавить проверки для других полей строки
                    return rowErrors
                }
            )

            // Если хотя бы в одной строке есть ошибка, добавляем массив ошибок в секцию
            if (rowsErrors.some((err) => Object.keys(err).length > 0)) {
                purchaseItemsSectionErrors.rows = rowsErrors
            }
        }
        // -------------------------------

        setErrors({
            purchaseContract: purchaseContractErrors,
            purchaseMethod: purchaseMethodErrors,
            purchaseChangeInfo: purchaseChangeInfoErrors,
            purchaseBudgetFunds: purchaseBudgetFundsErrors,
            purchasePaymentBreakdown: purchasePaymentBreakdownErrors,
            purchaseItemsSection: purchaseItemsSectionErrors,
            purchaseDatesInfo: purchaseDatesInfoErrors,
        })

        // Если роль admin или specialist, учитываем оба блока, иначе только purchaseContract
        const isPurchaseContractValid =
            Object.keys(purchaseContractErrors).length === 0
        const isPurchaseChangeInfoValid =
            Object.keys(purchaseChangeInfoErrors).length === 0
        const isPurchaseBudgetFundsValid =
            Object.keys(purchaseBudgetFundsErrors).length === 0
        const isPurchaseItemsSectionValid =
            Object.keys(purchaseItemsSectionErrors).length === 0
        const isPurchasePaymentBreakdownValid =
            Object.keys(purchasePaymentBreakdownErrors).length === 0
        const isPurchaseDatesInfoValid =
            Object.keys(purchaseDatesInfoErrors).length === 0
        const isPurchaseMethodValid =
            isUserRoleName === 'admin' || isUserRoleName === 'specialist'
                ? Object.keys(purchaseMethodErrors).length === 0
                : true

        // Сохраение после проверки
        handleSave()

        setErrorPopup(true)
        console.log(errors)

        return (
            isPurchaseContractValid &&
            isPurchaseMethodValid &&
            isPurchaseChangeInfoValid &&
            isPurchaseBudgetFundsValid &&
            isPurchasePaymentBreakdownValid &&
            isPurchaseItemsSectionValid &&
            isPurchaseDatesInfoValid
        )
    }

    const handleValidation = () => {
        if (validateForm()) {
            // Если форма валидна, можно выполнять дальнейшие действия (например, отправку данных)
            setHaveError(false)
            console.log('Форма валидна')
        } else {
            setHaveError(true)
            console.log('Найдены ошибки в форме')
        }
    }

    // Callback, который будет вызываться при изменении строк в дочернем компоненте
    const handleRowsChange = (rows: PlanPositionRow[]) => {
        setPurchaseItemsRows(rows)
    }

    const handleHaveError = (value: boolean) => {
        setHaveError(value)
    }

    // Рекурсивная функция для обхода объекта ошибок и сбора строковых сообщений
    const flattenErrors = (obj: any): string[] => {
        let errorList: string[] = []
        Object.keys(obj).forEach((key) => {
            const value = obj[key]
            if (typeof value === 'string') {
                errorList.push(value)
            } else if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (typeof item === 'string') {
                        errorList.push(item)
                    } else if (typeof item === 'object' && item !== null) {
                        errorList = errorList.concat(flattenErrors(item))
                    }
                })
            } else if (typeof value === 'object' && value !== null) {
                errorList = errorList.concat(flattenErrors(value))
            }
        })
        return errorList
    }

    // Функция для получения строки ошибок в нужном порядке, с изменённым именем переменной
    const getErrorsText = (errorObj: PurchaseEditErrors): string => {
        // Задаём порядок вывода ошибок
        const order: (keyof PurchaseEditErrors)[] = [
            'purchaseChangeInfo',
            'purchaseContract',
            'purchaseBudgetFunds',
            'purchaseDatesInfo',
            'purchaseMethod',
            'purchasePaymentBreakdown',
            'purchaseItemsSection',
        ]

        let errorList: string[] = []
        order.forEach((key) => {
            if (errorObj[key]) {
                errorList = errorList.concat(flattenErrors(errorObj[key]))
            }
        })
        // Объединяем каждое сообщение с новой строки
        return errorList.join('\n\n')
    }

    return (
        <div className=" flex flex-col w-full h-screen items-start justify-start ">
            <div className="flex flex-col w-full h-[160px] items-center justify-center">
                {/* <header className="p-5 text-[#181818] dark:text-[#FFFFFF]">{`${pos.position_stage} - ${pos.position_status}`}</header> */}
                <div className="flex flex-col w-full h-[90px] items-center justify-center bg-[#e5e5e5] dark:bg-[#000000]">
                    <PositionsStage planPosition={pos} theme={theme} />
                </div>
                <div className="flex flex-col w-full h-[70px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <PositionsInfo
                        planPosition={pos}
                        theme={theme}
                        planYear={planYear}
                    />
                </div>
            </div>

            <div className="grow flex flex-row w-full h-[calc(100vh_-_200px)] items-start justify-start gap-5 px-5 py-5 bg-[#f0f3f5] dark:bg-[#181818]">
                <div className="flex flex-col min-w-[1270px] max-w-[1270px] h-full gap-y-12 items-start justify-start overflow-x-visible overflow-y-scroll custom-scrollbar pb-5">
                    <fieldset
                        className="flex flex-col w-full"
                        disabled={
                            isApprove || // Закрыто для всех, независимо от роли
                            ((pos.position_status === 'Готово' ||
                                pos.position_stage != 'Редактирование') &&
                                isUserRoleName != 'admin' &&
                                isUserRoleName != 'specialist')
                        }
                    >
                        <PurchaseBasicInfo
                            regNumber={reg_number}
                            posGuid={pos.guid}
                            posStatus={pos.status}
                            fxdGuid={pos.expense_items_guid}
                            structShortName={
                                pos.struct_name || isUserStructName
                            }
                            structShortGuid={
                                pos.struct_guid || isUserStructGuid
                            }
                            onStructChange={({ structName, structGuid }) => {
                                setPos((prev) => ({
                                    ...prev,
                                    struct_guid: structGuid,
                                    struct_name: structName,
                                }))
                            }}
                            expensItemName={pos.expense_items_name}
                            onExpensItemsChange={({ fxdName, fxdGuid }) => {
                                setPos((prev) => ({
                                    ...prev,
                                    expense_items_name: fxdName,
                                    expense_items_guid: fxdGuid,
                                }))
                            }}
                            typeObjectPurchasePosition={
                                pos.type_object_purchase_position
                            }
                            onTypeObjectPurchasePositionChange={(name) => {
                                setPos((prev) => ({
                                    ...prev,
                                    type_object_purchase_position: name,
                                }))
                            }}
                            proposedMethodName={pos.proposed_method_name}
                            onProposedMethodChange={(name) => {
                                setPos((prev) => ({
                                    ...prev,
                                    proposed_method_name: name,
                                }))
                            }}
                            basisSingleSupplier={
                                pos.exclusive_purchase_reason_name
                            }
                            onBasisSingleSupplierChange={(name) => {
                                setPos((prev) => ({
                                    ...prev,
                                    exclusive_purchase_reason_name: name,
                                }))
                            }}
                            annotationPurchase={pos.annotation}
                            onAnnotationPurchaseChange={(annotation) => {
                                setPos((prev) => ({
                                    ...prev,
                                    annotation: annotation,
                                }))
                            }}
                            multiSelectDisabled={
                                isApprove || // Закрыто для всех, независимо от роли
                                ((pos.position_status === 'Готово' ||
                                    pos.position_stage != 'Редактирование') &&
                                    isUserRoleName != 'admin' &&
                                    isUserRoleName != 'specialist')
                            }
                        />
                    </fieldset>

                    <fieldset
                        className="flex flex-col w-full"
                        disabled={
                            pos.status === 'P' ||
                            pos.position_status === 'Готово к отправке' ||
                            pos.position_status === 'Отправлено в ЕИС' ||
                            pos.position_status === 'Готово' ||
                            isApprove || // Закрыто для всех, независимо от роли
                            ((pos.position_status === 'Готово' ||
                                pos.position_stage != 'Редактирование') &&
                                (pos.user_guid != user?.object.user_id ||
                                    pos.supervisor_user_guid !=
                                        user?.object.user_id) &&
                                isUserRoleName != 'admin' &&
                                isUserRoleName != 'specialist')
                        }
                    >
                        <main className="grow flex flex-col gap-y-12">
                            {pos.status !== 'N' &&
                                (pos.status === 'C' ||
                                    pos.changed_GWS_and_dates ||
                                    pos.changed_NMSK_more_ten_percent ||
                                    pos.other_changes ||
                                    pos.modification_description) && (
                                    <PurchaseChangeInfo
                                        changedGWSAndDates={
                                            pos.changed_GWS_and_dates
                                        }
                                        onChangedGWSAndDatesChange={(value) => {
                                            setPos((prev) => ({
                                                ...prev,
                                                changed_GWS_and_dates: value,
                                            }))
                                            setHaveError(true)
                                        }}
                                        changedNMSKMoreTenPercent={
                                            pos.changed_NMSK_more_ten_percent
                                        }
                                        onChangedNMSKMoreTenPercentChange={(
                                            value
                                        ) => {
                                            setPos((prev) => ({
                                                ...prev,
                                                changed_NMSK_more_ten_percent:
                                                    value,
                                            }))
                                            setHaveError(true)
                                        }}
                                        otherChanges={pos.other_changes}
                                        onOtherChangesChange={(value) => {
                                            setPos((prev) => ({
                                                ...prev,
                                                other_changes: value,
                                            }))
                                            setHaveError(true)
                                        }}
                                        modificationDescription={
                                            pos.modification_description
                                        }
                                        onModificationDescriptionChange={(
                                            value
                                        ) => {
                                            setPos((prev) => ({
                                                ...prev,
                                                modification_description: value,
                                            }))
                                            setHaveError(true)
                                        }}
                                        errors={errors.purchaseChangeInfo}
                                    />
                                )}

                            <PurchaseContractInfo
                                name={pos.contract_subject}
                                theme={theme}
                                onNameChange={(name) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        contract_subject: name,
                                    }))
                                    setHaveError(true)
                                }}
                                minRequirements={pos.minimum_requirements || ''}
                                onMinRequirementsChange={(minRequirements) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        minimum_requirements: minRequirements,
                                    }))
                                    setHaveError(true)
                                }}
                                contractPriceReason={pos.order_pricing || ''}
                                onContractPriceReasonChange={(
                                    contractPriceReason
                                ) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        order_pricing: contractPriceReason,
                                    }))
                                    setHaveError(true)
                                }}
                                nmcd={
                                    pos.maximum_contract_price
                                        ? Number(pos.maximum_contract_price)
                                        : 0
                                }
                                onNmcdChange={(newNmcd) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        maximum_contract_price: newNmcd,
                                    }))
                                    setHaveError(true)
                                }}
                                currency={pos.currency_digital_code}
                                onCurrencyChange={async (
                                    newCurrencyDigitalCode
                                ) => {
                                    const updated = await onCurrencyUpdate(
                                        pos,
                                        newCurrencyDigitalCode,
                                        getCurrentDate()
                                    )

                                    setPos((prev) => ({
                                        ...prev,
                                        ...updated,
                                    }))
                                    setHaveError(true)
                                }}
                                isNmcdWithTax={pos.is_nmcd_with_tax}
                                onNmcdWithTaxChange={(newNmcdWithTax) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        is_nmcd_with_tax: newNmcdWithTax,
                                    }))
                                    setHaveError(true)
                                }}
                                currencyExchangeDate={
                                    pos.exchange_rate_date
                                        ? new Date(pos.exchange_rate_date)
                                        : undefined
                                }
                                onCurrencyExchangeDateChange={
                                    handleCurrencyExchangeDateChange
                                }
                                currencyExchangeRate={pos.exchange_rate ?? 1}
                                onCurrencyExchangeRateChange={
                                    handleCurrencyExchangeRateChange
                                }
                                currencyData={currencyList}
                                posAfterSecondYear={
                                    pos.planned_after_second_year
                                }
                                isMspSecondYear={
                                    getStartDate(pos).getFullYear() -
                                        plan[0]?.reporting_year ==
                                        1 && pos.is_purchase_smb == true
                                }
                                errors={errors.purchaseContract} // передаём ошибки, относящиеся к PurchaseContractInfo
                            />

                            {!(
                                pos.planned_after_second_year ||
                                (getStartDate(pos).getFullYear() -
                                    plan[0]?.reporting_year ==
                                    1 &&
                                    pos.is_purchase_smb == true)
                            ) && (
                                <PurchaseBudgetFunds
                                    isBudgetFundsIncluded={
                                        pos.maximum_contract_price_from_budget
                                    }
                                    onBudgetFundsIncludedChange={
                                        handleBudgetFundsChange
                                    }
                                    positionFinancialProvisionVolumeItems={
                                        pos.position_financial_provision_volumes
                                    }
                                    onPositionFinancialProvisionChange={
                                        handlePositionFinancialProvisionChange
                                    }
                                    isDraft={
                                        pos.position_status !== 'Размещена'
                                    }
                                    disabledFieldset={
                                        pos.status === 'P' ||
                                        pos.position_status ===
                                            'Готово к отправке' ||
                                        pos.position_status ===
                                            'Отправлено в ЕИС' ||
                                        pos.position_status === 'Готово' ||
                                        isApprove || // Закрыто для всех, независимо от роли
                                        ((pos.position_status === 'Готово' ||
                                            pos.position_stage !=
                                                'Редактирование' ||
                                            pos.user_guid !=
                                                user?.object.user_id ||
                                            pos.supervisor_user_guid !=
                                                user?.object.user_id) &&
                                            isUserRoleName != 'admin' &&
                                            isUserRoleName != 'specialist')
                                    }
                                    errors={errors.purchaseBudgetFunds} // передаём ошибки, относящиеся к PurchaseContractInfo
                                    theme={theme}
                                />
                            )}

                            {!pos.planned_after_second_year && (
                                <PurchaseDatesInfo
                                    start={purchasePlannedDate}
                                    theme={theme}
                                    onStartDateChange={(date, isFullDate) => {
                                        if (
                                            date.getFullYear() -
                                                plan[0]?.reporting_year >=
                                                2 &&
                                            pos.is_purchase_smb == true
                                        ) {
                                            setPos((prev) => ({
                                                ...prev,
                                                planned_after_second_year: true,
                                            }))
                                        }
                                        setPos((prev) =>
                                            onDateUpdate(
                                                prev,
                                                true /* isStart */,
                                                date,
                                                isFullDate,
                                                plan[0]?.reporting_year
                                            )
                                        )
                                    }}
                                    end={contractEndDate}
                                    onEndDateChange={(date) => {
                                        setPos((prev) =>
                                            onDateUpdate(
                                                prev,
                                                false /* isStart */,
                                                date,
                                                false,
                                                plan[0]?.reporting_year
                                            )
                                        )
                                    }}
                                    isStartFullDate={
                                        pos.purchase_planned_date !== null
                                    }
                                    planYear={plan[0]?.start_date.split('-')[0]}
                                    onLongTerm={(value) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            long_term: value,
                                        }))
                                    }}
                                    errors={errors.purchasePaymentBreakdown} // передаём ошибки, относящиеся к PurchaseContractInfo
                                    errorsDatesInfo={errors.purchaseDatesInfo} // передаём ошибки, относящиеся к PurchaseContractInfo
                                />
                            )}

                            {pos.long_term_volumes !== null &&
                                pos.long_term_smb_volumes != null && (
                                    <div className="flex flex-row gap-x-12">
                                        <div className="basis-1/2">
                                            {pos.long_term_volumes?.details
                                                ?.items && (
                                                <PurchasePaymentBreakdown
                                                    title="Информация об объемах оплаты долгосрочного договора"
                                                    yearlyPayments={
                                                        pos.long_term_volumes
                                                    }
                                                    onYearlyPaymentsChange={(
                                                        year,
                                                        value
                                                    ) => {
                                                        setHaveError(true)
                                                        const normalizedValue =
                                                            value || 0 // Заменяем пустое значение на 0
                                                        if (
                                                            pos.long_term_volumes
                                                        ) {
                                                            const item = (
                                                                pos
                                                                    .long_term_volumes
                                                                    .details
                                                                    ?.items ??
                                                                []
                                                            ).find(
                                                                (y) =>
                                                                    y.year ===
                                                                    year
                                                            )
                                                            if (item) {
                                                                setPos(
                                                                    (prev) => {
                                                                        if (
                                                                            prev.long_term_volumes ===
                                                                            null
                                                                        ) {
                                                                            return prev
                                                                        }

                                                                        // Округляем newVolume, чтобы избежать ошибок с плавающей точкой
                                                                        const newVolume =
                                                                            Math.round(
                                                                                (prev
                                                                                    .long_term_volumes
                                                                                    .volume -
                                                                                    item.summ +
                                                                                    normalizedValue) *
                                                                                    100
                                                                            ) /
                                                                            100

                                                                        const updatedItems =
                                                                            (
                                                                                prev
                                                                                    .long_term_volumes
                                                                                    .details
                                                                                    ?.items ??
                                                                                []
                                                                            ).map(
                                                                                (
                                                                                    dt
                                                                                ) => {
                                                                                    if (
                                                                                        dt.year ===
                                                                                        year
                                                                                    ) {
                                                                                        // Округляем summRub для предотвращения ошибок при пересчёте суммы в рубли
                                                                                        const summRub =
                                                                                            prev.currency_digital_code !==
                                                                                            643
                                                                                                ? Math.round(
                                                                                                      normalizedValue *
                                                                                                          (prev.exchange_rate ||
                                                                                                              1) *
                                                                                                          100
                                                                                                  ) /
                                                                                                  100
                                                                                                : undefined

                                                                                        return {
                                                                                            ...dt,
                                                                                            summ: normalizedValue,
                                                                                            ...(summRub !==
                                                                                                undefined && {
                                                                                                summ_rub:
                                                                                                    summRub,
                                                                                            }),
                                                                                        }
                                                                                    }

                                                                                    return dt
                                                                                }
                                                                            )

                                                                        // Округляем volumeRub для точности отображения итоговой суммы в рублях
                                                                        const volumeRub =
                                                                            prev.currency_digital_code !==
                                                                            643
                                                                                ? Math.round(
                                                                                      newVolume *
                                                                                          (prev.exchange_rate ||
                                                                                              1) *
                                                                                          100
                                                                                  ) /
                                                                                  100
                                                                                : undefined

                                                                        const currency =
                                                                            pos.currency_digital_code !=
                                                                            643
                                                                                ? {
                                                                                      code: pos.currency_code,
                                                                                      name: pos.currency_name,
                                                                                      digital_code:
                                                                                          pos.currency_digital_code,
                                                                                  }
                                                                                : {
                                                                                      code: 'RUB',
                                                                                      name: 'Российский рубль',
                                                                                      digital_code: 643,
                                                                                  }

                                                                        const exchange_rate =
                                                                            pos.currency_digital_code !=
                                                                            643
                                                                                ? pos.exchange_rate
                                                                                : null

                                                                        return {
                                                                            ...prev,
                                                                            long_term_volumes:
                                                                                {
                                                                                    ...prev.long_term_volumes,
                                                                                    volume: newVolume,
                                                                                    volume_rub:
                                                                                        volumeRub,
                                                                                    details:
                                                                                        {
                                                                                            items: updatedItems,
                                                                                        },
                                                                                    currency:
                                                                                        currency,
                                                                                    exchange_rate:
                                                                                        exchange_rate,
                                                                                },
                                                                        }
                                                                    }
                                                                )
                                                            }
                                                        }
                                                    }}
                                                    currency={
                                                        pos.currency_digital_code
                                                    }
                                                    currencyExchangeDate={
                                                        pos.exchange_rate_date
                                                            ? new Date(
                                                                  pos.exchange_rate_date
                                                              )
                                                            : undefined
                                                    }
                                                    currencyExchangeRate={
                                                        pos.exchange_rate ??
                                                        undefined
                                                    }
                                                    currencyList={currencyList}
                                                    errors={
                                                        errors.purchasePaymentBreakdown
                                                    } // передаём ошибки, относящиеся к PurchaseContractInfo
                                                />
                                            )}
                                        </div>
                                        <div className="basis-1/2">
                                            {pos.long_term_smb_volumes?.details
                                                ?.items && (
                                                <PurchasePaymentBreakdown
                                                    title="Информация об объемах привлечения субъектов МСП"
                                                    yearlyPayments={
                                                        pos.long_term_smb_volumes
                                                    }
                                                    onYearlyPaymentsChange={(
                                                        year,
                                                        value
                                                    ) => {
                                                        setHaveError(true)
                                                        const normalizedValue =
                                                            value || 0 // Заменяем пустое значение на 0
                                                        if (
                                                            pos.long_term_smb_volumes
                                                        ) {
                                                            const item = (
                                                                pos
                                                                    .long_term_smb_volumes
                                                                    .details
                                                                    ?.items ??
                                                                []
                                                            ).find(
                                                                (y) =>
                                                                    y.year ===
                                                                    year
                                                            )
                                                            if (item) {
                                                                setPos(
                                                                    (prev) => {
                                                                        if (
                                                                            prev.long_term_smb_volumes ===
                                                                            null
                                                                        ) {
                                                                            return prev
                                                                        }

                                                                        // Округляем newVolume, чтобы избежать ошибок с плавающей точкой
                                                                        const newVolume =
                                                                            Math.round(
                                                                                (prev
                                                                                    .long_term_smb_volumes
                                                                                    .volume -
                                                                                    item.summ +
                                                                                    normalizedValue) *
                                                                                    100
                                                                            ) /
                                                                            100

                                                                        const updatedItems =
                                                                            (
                                                                                prev
                                                                                    .long_term_smb_volumes
                                                                                    .details
                                                                                    ?.items ??
                                                                                []
                                                                            ).map(
                                                                                (
                                                                                    dt
                                                                                ) => {
                                                                                    if (
                                                                                        dt.year ===
                                                                                        year
                                                                                    ) {
                                                                                        // Округляем summRub для предотвращения ошибок при пересчёте суммы в рубли
                                                                                        const summRub =
                                                                                            prev.currency_digital_code !==
                                                                                            643
                                                                                                ? Math.round(
                                                                                                      normalizedValue *
                                                                                                          (prev.exchange_rate ||
                                                                                                              1) *
                                                                                                          100
                                                                                                  ) /
                                                                                                  100
                                                                                                : undefined

                                                                                        return {
                                                                                            ...dt,
                                                                                            summ: normalizedValue,
                                                                                            ...(summRub !==
                                                                                                undefined && {
                                                                                                summ_rub:
                                                                                                    summRub,
                                                                                            }),
                                                                                        }
                                                                                    }

                                                                                    return dt
                                                                                }
                                                                            )

                                                                        // Округляем volumeRub для точности отображения итоговой суммы в рублях
                                                                        const volumeRub =
                                                                            prev.currency_digital_code !==
                                                                            643
                                                                                ? Math.round(
                                                                                      newVolume *
                                                                                          (prev.exchange_rate ||
                                                                                              1) *
                                                                                          100
                                                                                  ) /
                                                                                  100
                                                                                : undefined

                                                                        const currency =
                                                                            pos.currency_digital_code !=
                                                                            643
                                                                                ? {
                                                                                      code: pos.currency_code,
                                                                                      name: pos.currency_name,
                                                                                      digital_code:
                                                                                          pos.currency_digital_code,
                                                                                  }
                                                                                : {
                                                                                      code: 'RUB',
                                                                                      name: 'Российский рубль',
                                                                                      digital_code: 643,
                                                                                  }

                                                                        const exchange_rate =
                                                                            pos.currency_digital_code !=
                                                                            643
                                                                                ? pos.exchange_rate
                                                                                : null

                                                                        return {
                                                                            ...prev,
                                                                            long_term_smb_volumes:
                                                                                {
                                                                                    ...prev.long_term_smb_volumes,
                                                                                    volume: newVolume,
                                                                                    volume_rub:
                                                                                        volumeRub,
                                                                                    details:
                                                                                        {
                                                                                            items: updatedItems,
                                                                                        },
                                                                                    currency:
                                                                                        currency,
                                                                                    exchange_rate:
                                                                                        exchange_rate,
                                                                                },
                                                                        }
                                                                    }
                                                                )
                                                            }
                                                        }
                                                    }}
                                                    currency={
                                                        pos.currency_digital_code
                                                    }
                                                    currencyExchangeDate={
                                                        pos.exchange_rate_date
                                                            ? new Date(
                                                                  pos.exchange_rate_date
                                                              )
                                                            : undefined
                                                    }
                                                    currencyExchangeRate={
                                                        pos.exchange_rate ??
                                                        undefined
                                                    }
                                                    currencyList={currencyList}
                                                    errors={
                                                        errors.purchasePaymentBreakdown
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                            {isUserRoleName == 'admin' ||
                            isUserRoleName == 'specialist' ||
                            pos.position_stage == 'План закупки в ЕИС' ? (
                                <PurchaseMethod
                                    posStatus={pos.status}
                                    posStage={pos.position_stage}
                                    methodCode={pos.purchase_method_code}
                                    onMethodCodeChange={(code) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            purchase_method_code: code,
                                        }))
                                        setHaveError(true)
                                    }}
                                    method={pos.purchase_method_name}
                                    onMethodChange={(name) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            purchase_method_name: name,
                                        }))
                                    }}
                                    isElectronic={pos.is_electronic}
                                    onIsElectronicChange={(electronic) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            is_electronic: electronic,
                                        }))
                                    }}
                                    basisSingleSupplier={
                                        pos.single_purchase_reason_name
                                    }
                                    onBasisSingleSupplierChange={(name) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            single_purchase_reason_name: name,
                                        }))
                                    }}
                                    areParticipantsSME={pos.is_purchase_smb}
                                    onAreParticipantsSMEChange={(
                                        isPurchaseSmb
                                    ) => {
                                        if (isPurchaseSmb == true) {
                                            if (
                                                plan[0]?.reporting_year <
                                                getStartDate(pos).getFullYear()
                                            ) {
                                                if (
                                                    getStartDate(
                                                        pos
                                                    ).getFullYear() -
                                                        plan[0]
                                                            ?.reporting_year >=
                                                    2
                                                ) {
                                                    setPos((prev) => ({
                                                        ...prev,
                                                        is_purchase_smb:
                                                            isPurchaseSmb,
                                                        long_term_volumes: null,
                                                        long_term_smb_volumes:
                                                            null,
                                                        is_purchase_ignored:
                                                            false,
                                                        purchase_category: null,
                                                        purchase_method_code:
                                                            null,
                                                        purchase_method_name:
                                                            null,
                                                        single_purchase_reason_guid:
                                                            null,
                                                        single_purchase_reason_name:
                                                            null,
                                                        order_pricing: null,
                                                        planned_after_second_year:
                                                            true,
                                                        position_financial_provision_volumes:
                                                            null,
                                                        maximum_contract_price_from_budget:
                                                            false,
                                                    }))
                                                } else {
                                                    setPos((prev) => ({
                                                        ...prev,
                                                        is_purchase_smb:
                                                            isPurchaseSmb,
                                                        long_term_volumes: null,
                                                        long_term_smb_volumes:
                                                            null,
                                                        is_purchase_ignored:
                                                            false,
                                                        purchase_category: null,
                                                        purchase_method_code:
                                                            null,
                                                        purchase_method_name:
                                                            null,
                                                        single_purchase_reason_guid:
                                                            null,
                                                        single_purchase_reason_name:
                                                            null,
                                                        order_pricing: null,
                                                        planned_after_second_year:
                                                            false,
                                                        position_financial_provision_volumes:
                                                            null,
                                                        maximum_contract_price_from_budget:
                                                            false,
                                                    }))
                                                }
                                            } else {
                                                setPos((prev) => ({
                                                    ...prev,
                                                    is_purchase_smb:
                                                        isPurchaseSmb,
                                                }))
                                            }
                                        } else {
                                            setPos((prev) => ({
                                                ...prev,
                                                is_purchase_smb: isPurchaseSmb,
                                                planned_after_second_year:
                                                    false,
                                            }))
                                            setPos((prev) =>
                                                onDateUpdate(
                                                    prev,
                                                    false /* isStart */,
                                                    pos.contract_end_date
                                                        ? new Date(
                                                              pos.contract_end_date
                                                          )
                                                        : new Date(
                                                              getCurrentYear(),
                                                              getCurrentMonth(),
                                                              1
                                                          ),
                                                    false,
                                                    plan[0]?.reporting_year
                                                )
                                            )
                                        }
                                        setHaveError(true)
                                    }}
                                    isPlannedThirdYear={
                                        pos.planned_after_second_year
                                    }
                                    onIsPlannedThirdYearChange={(
                                        plannedAfterSecondYear
                                    ) => {
                                        if (plannedAfterSecondYear == true) {
                                            setPos((prev) => ({
                                                ...prev,
                                                planned_after_second_year:
                                                    plannedAfterSecondYear,
                                                contract_end_date: null,
                                                order_pricing: null,
                                                maximum_contract_price: null,
                                                currency_digital_code: null,
                                                currency_code: null,
                                                currency_name: null,
                                                purchase_planned_date: null,
                                                purchase_period_year: null,
                                                purchase_period_month: null,
                                                purchase_period_quarter: null,
                                                long_term: false,
                                                long_term_volumes: null,
                                                long_term_smb_volumes: null,
                                                is_purchase_ignored: false,
                                                purchase_category: null,
                                                purchase_method_code: null,
                                                purchase_method_name: null,
                                                single_purchase_reason_guid:
                                                    null,
                                                single_purchase_reason_name:
                                                    null,
                                                position_financial_provision_volumes:
                                                    null,
                                                maximum_contract_price_from_budget:
                                                    false,
                                            }))
                                        } else {
                                            setPos((prev) => ({
                                                ...prev,
                                                planned_after_second_year:
                                                    plannedAfterSecondYear,
                                                contract_end_date:
                                                    getCurrentDate(),
                                                purchase_period_year:
                                                    getCurrentYear(),
                                                purchase_period_month:
                                                    getCurrentMonth() + 1,
                                                order_pricing:
                                                    'В соответствии с документацией о закупке',
                                                maximum_contract_price: 0,
                                                currency_digital_code: 643,
                                                currency_code: 'RUB',
                                                currency_name:
                                                    'Российский рубль',
                                            }))
                                            onDateUpdate(
                                                pos,
                                                true,
                                                new Date(
                                                    getCurrentYear(),
                                                    getCurrentMonth(),
                                                    1
                                                ),
                                                false,
                                                plan[0]?.reporting_year
                                            )
                                        }
                                    }}
                                    isInnovativeProduct={
                                        pos.innovation_equivalent
                                    }
                                    onIsInnovativeProductChange={(
                                        innovationEquivalent
                                    ) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            innovation_equivalent:
                                                innovationEquivalent,
                                        }))
                                    }}
                                    isExcludedForSME={pos.is_purchase_ignored}
                                    onIsExcludedForSMEChange={(
                                        isPurchaseIgnored
                                    ) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            is_purchase_ignored:
                                                isPurchaseIgnored,
                                        }))
                                        setHaveError(true)
                                    }}
                                    category={pos.purchase_category}
                                    onCategoryChange={(category) => {
                                        setPos((prev) => ({
                                            ...prev,
                                            purchase_category: category,
                                        }))
                                    }}
                                    posMSPStatus={pos.is_purchase_smb}
                                    isOkpd2Have={isOkpd2Have}
                                    isHaveNotMspOkpd2={isHaveNotMspOkpd2}
                                    errors={errors.purchaseMethod}
                                    orgData={orgData}
                                    isMspSecondYear={
                                        getStartDate(pos).getFullYear() -
                                            plan[0]?.reporting_year ==
                                            1 && pos.is_purchase_smb == true
                                    }
                                    multiSelectDisabled={
                                        pos.status === 'P' ||
                                        pos.position_status ===
                                            'Готово к отправке' ||
                                        pos.position_status ===
                                            'Отправлено в ЕИС' ||
                                        pos.position_status === 'Готово' ||
                                        isApprove || // Закрыто для всех, независимо от роли
                                        ((pos.position_status === 'Готово' ||
                                            pos.position_stage !=
                                                'Редактирование') &&
                                            (pos.user_guid !=
                                                user?.object.user_id ||
                                                pos.supervisor_user_guid !=
                                                    user?.object.user_id) &&
                                            isUserRoleName != 'admin' &&
                                            isUserRoleName != 'specialist')
                                    }
                                />
                            ) : (
                                ''
                            )}

                            <PurchaseItemsSection
                                regNumber={reg_number}
                                posGuid={pos.guid}
                                posMSPStatus={pos.is_purchase_smb}
                                purchaseMethodCode={pos.purchase_method_code}
                                purchaseMethodName={pos.purchase_method_name}
                                isDraft={pos.position_status !== 'Размещена'} // TODO: Iosif to check this (pos.position_status === 'Черновик')
                                isGeneralAddress={pos.is_general_address}
                                onIsGeneralAddressChange={(generalAddress) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        is_general_address: generalAddress,
                                    }))
                                    setHaveError(true)
                                }}
                                okatoCode={pos.okato}
                                onOkatoCodeChange={(okato, name) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        okato: okato,
                                        region: name,
                                    }))
                                    setHaveError(true)
                                }}
                                region={pos.region}
                                onRegionChange={(name) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        region: name,
                                    }))
                                    setHaveError(true)
                                }}
                                onAreParticipantsSMEChange={(isPurchaseSmb) => {
                                    if (isPurchaseSmb == true) {
                                        if (
                                            plan[0]?.reporting_year <
                                            getStartDate(pos).getFullYear()
                                        ) {
                                            setPos((prev) => ({
                                                ...prev,
                                                is_purchase_smb: isPurchaseSmb,
                                                long_term_volumes: null,
                                                long_term_smb_volumes: null,
                                                is_purchase_ignored: false,
                                                purchase_category: null,
                                                purchase_method_code: null,
                                                purchase_method_name: null,
                                                single_purchase_reason_guid:
                                                    null,
                                                single_purchase_reason_name:
                                                    null,
                                            }))
                                        } else {
                                            setPos((prev) => ({
                                                ...prev,
                                                is_purchase_smb: isPurchaseSmb,
                                            }))
                                        }
                                    } else {
                                        setPos((prev) => ({
                                            ...prev,
                                            is_purchase_smb: isPurchaseSmb,
                                        }))
                                        setPos((prev) =>
                                            onDateUpdate(
                                                prev,
                                                false /* isStart */,
                                                pos.contract_end_date
                                                    ? new Date(
                                                          pos.contract_end_date
                                                      )
                                                    : new Date(
                                                          getCurrentYear(),
                                                          getCurrentMonth(),
                                                          1
                                                      ),
                                                false,
                                                plan[0]?.reporting_year
                                            )
                                        )
                                    }
                                    setHaveError(true)
                                }}
                                onOkpdHave={handleOkpd2Have}
                                onHaveNotMspOkpd2={handleHaveNotMspOkpd2}
                                positionStatus={pos.position_status}
                                positionStage={pos.position_stage}
                                disabledFieldset={
                                    pos.status === 'P' ||
                                    pos.position_status ===
                                        'Готово к отправке' ||
                                    pos.position_status ===
                                        'Отправлено в ЕИС' ||
                                    pos.position_status === 'Готово' ||
                                    isApprove || // Закрыто для всех, независимо от роли
                                    ((pos.position_status === 'Готово' ||
                                        pos.position_stage !=
                                            'Редактирование') &&
                                        (pos.user_guid !=
                                            user?.object.user_id ||
                                            pos.supervisor_user_guid !=
                                                user?.object.user_id) &&
                                        isUserRoleName != 'admin' &&
                                        isUserRoleName != 'specialist')
                                }
                                onRowsChange={handleRowsChange}
                                onHaveError={handleHaveError}
                                errors={errors.purchaseItemsSection}
                                posTypeObjectPurchase={
                                    pos.type_object_purchase_position
                                }
                                onTypeObjectPurchasePositionChange={(name) => {
                                    setPos((prev) => ({
                                        ...prev,
                                        type_object_purchase_position: name,
                                    }))
                                }}
                                orgData={orgData}
                                theme={theme}
                                isMspSecondYear={
                                    getStartDate(pos).getFullYear() -
                                        plan[0]?.reporting_year ==
                                        1 && pos.is_purchase_smb == true
                                }
                                plannedAfterSecondYear={
                                    pos.planned_after_second_year
                                }
                            />
                        </main>
                    </fieldset>
                </div>

                {/* блок согласования */}
                {/* <ApprovalsList
                    pos={pos}
                    user={user?.object.user_id}
                    structList={structList}
                    userList={userList}
                />

                <br /> */}

                <div className="flex flex-col min-w-[580px] h-full rounded-lg gap-5 items-center justify-start px-5 py-5 overflow-y-auto custom-scrollbar bg-[#e5e5e5] dark:bg-[#262427] shadow-lg shadow-[#18181825]">
                    <div className="flex flex-row w-full gap-2">
                        <div
                            className={`flex flex-col w-fit py-2 px-5 cursor-pointer text-[14px] hover:border-b-2 hover:border-[#494849] ${infoTab == 'approval' ? 'text-[#181818] dark:text-[#FFFFFF] border-b-2 border-[#494849]' : 'text-[#6A6A6A] dark:text-[#8A8A8A] hover:text-[#181818] hover:dark:text-[#FFFFFF]'}`}
                            onClick={() => setInfoTab('approval')}
                        >
                            Согласование
                        </div>
                        <div
                            className={`flex flex-col w-fit py-2 px-5 cursor-pointer text-[14px] hover:border-b-2 hover:border-[#494849] ${infoTab == 'info' ? 'text-[#181818] dark:text-[#FFFFFF] border-b-2 border-[#494849]' : 'text-[#6A6A6A] dark:text-[#8A8A8A] hover:text-[#181818] hover:dark:text-[#FFFFFF]'}`}
                            onClick={() => setInfoTab('info')}
                        >
                            Информация
                        </div>
                    </div>
                    {infoTab == 'approval' ? (
                        <>
                            <div className="flex flex-row w-full p-3 rounded-md bg-[#dbdbdb] dark:bg-[#333033] items-center justify-start">
                                <button
                                    className={`flex flex-row gap-3 px-5 py-1 rounded-[10px] text-[12.8px]
                                    ${
                                        (pos.user_guid ===
                                            user?.object.user_id ||
                                            pos.supervisor_user_guid ===
                                                user?.object.user_id) &&
                                        !approvals.some(
                                            (approval) => approval.status === 0
                                        ) &&
                                        pos.position_status == 'Готово'
                                            ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#F6F6F6] hover:bg-[#FFFFFF] dark:bg-[#494849] dark:hover:bg-[#656365]'
                                            : 'text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#EDEDED] dark:bg-[#494849]'
                                    }`}
                                    onClick={() => {
                                        setApproversAdd([])
                                        setApprovalChangeGuid('')
                                        setApprovalAddGuid('')
                                        handleApprovalPopup()
                                    }}
                                    disabled={
                                        (pos.user_guid ===
                                            user?.object.user_id ||
                                            pos.supervisor_user_guid ===
                                                user?.object.user_id) &&
                                        !approvals.some(
                                            (approval) => approval.status === 0
                                        ) &&
                                        pos.position_status == 'Готово'
                                            ? false
                                            : true
                                    }
                                >
                                    Создать согласование
                                </button>
                            </div>
                            {approvals.map((approval, index) => (
                                <div
                                    key={index}
                                    className="flex flex-col p-3 gap-1 rounded-md bg-[#dbdbdb] dark:bg-[#2C2A2E] w-full"
                                >
                                    <div className="flex flex-row w-full gap-3 items-center justify-start">
                                        <div
                                            className={`flex flex-row w-4 h-4  rounded-full 
                                        ${
                                            theme == 'dark'
                                                ? approval.status == 0
                                                    ? 'bg-[#FAB502]'
                                                    : approval.status == 1 ||
                                                        approval.status == 2
                                                      ? 'bg-[#049664]'
                                                      : approval.status == 3
                                                        ? 'bg-[#C15C5C]'
                                                        : approval.status == 4
                                                          ? 'bg-[#464447]'
                                                          : ''
                                                : approval.status == 0
                                                  ? 'bg-[#FAB502]'
                                                  : approval.status == 1 ||
                                                      approval.status == 2
                                                    ? 'bg-[#83d2b7]'
                                                    : approval.status == 3
                                                      ? 'bg-[#ed9cae]'
                                                      : approval.status == 4
                                                        ? 'bg-[#d0d0d0]'
                                                        : ''
                                        }`}
                                        ></div>
                                        <div className="flex flex-row py-1 text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                                            {approval.status == 0
                                                ? 'Идет согласование'
                                                : approval.status == 1 ||
                                                    approval.status == 2
                                                  ? 'Согласовано'
                                                  : approval.status == 3
                                                    ? 'Не согласовано'
                                                    : approval.status == 4
                                                      ? 'Отменена'
                                                      : ''}
                                        </div>
                                        <div className="flex flex-1 w-full items-center justify-end">
                                            <HoverTooltip
                                                tooltipText={`${expandedIndex === index ? 'Скрыть' : 'Показать'}`}
                                            >
                                                <div
                                                    className="flex flex-col w-6 h-6 rounded-full cursor-pointer items-center justify-center text-[#181818] dark:text-[#FFFFFF] bg-[#EDEDED] hover:bg-[#FFFFFF] dark:bg-[#494849] dark:hover:bg-[#656365]"
                                                    onClick={() =>
                                                        toggleAccordion(
                                                            index,
                                                            approval.guid
                                                        )
                                                    }
                                                >
                                                    {expandedIndex === index ? (
                                                        <svg
                                                            className="w-3 h-3"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 24 24"
                                                            id="down"
                                                        >
                                                            <path
                                                                fill={
                                                                    theme ==
                                                                    'dark'
                                                                        ? '#FFFFFF'
                                                                        : '#181818'
                                                                }
                                                                d="M17,13.41,12.71,9.17a1,1,0,0,0-1.42,0L7.05,13.41a1,1,0,0,0,0,1.42,1,1,0,0,0,1.41,0L12,11.29l3.54,3.54a1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29A1,1,0,0,0,17,13.41Z"
                                                            ></path>
                                                        </svg>
                                                    ) : (
                                                        <svg
                                                            className="w-3 h-3"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 24 24"
                                                            id="down"
                                                        >
                                                            <path
                                                                fill={
                                                                    theme ==
                                                                    'dark'
                                                                        ? '#FFFFFF'
                                                                        : '#181818'
                                                                }
                                                                d="M17,9.17a1,1,0,0,0-1.41,0L12,12.71,8.46,9.17a1,1,0,0,0-1.41,0,1,1,0,0,0,0,1.42l4.24,4.24a1,1,0,0,0,1.42,0L17,10.59A1,1,0,0,0,17,9.17Z"
                                                            ></path>
                                                        </svg>
                                                    )}
                                                </div>
                                            </HoverTooltip>
                                        </div>
                                    </div>
                                    <div className="flex flex-row w-full gap-3 items-center justify-start">
                                        <div className="flex flex-col w-full gap-1 items-start justify-start">
                                            <div className="flex flex-col w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                Начало согласования
                                            </div>
                                            <div className="flex w-full items-start justify-start">
                                                <input
                                                    type="date"
                                                    className="form-input w-full bg-[#1F1E1F]"
                                                    value={approval.start_date}
                                                    disabled
                                                ></input>
                                            </div>
                                        </div>
                                        <div className="flex flex-col w-full gap-1 items-start justify-start">
                                            <div className="flex flex-col w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                Срок окончания
                                            </div>
                                            <div className="flex w-full items-start justify-start">
                                                <input
                                                    type="date"
                                                    className="form-input w-full bg-[#1F1E1F]"
                                                    value={approval.end_date}
                                                    style={{
                                                        colorScheme:
                                                            theme === 'dark'
                                                                ? 'dark'
                                                                : 'light',
                                                    }}
                                                    disabled
                                                ></input>
                                            </div>
                                        </div>
                                        <div className="flex flex-col w-full gap-1 items-start justify-start">
                                            <div className="flex flex-col w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                Приоритет
                                            </div>
                                            <div className="flex w-full items-start justify-start">
                                                <select
                                                    className={`flex px-2 py-1 h-[28px] rounded-md text-sm w-full 
                                                ${
                                                    approval.priority == 0
                                                        ? 'text-[#181818] dark:text-[#FFFFFF] border border-[#b8b8b8] dark:border-[#2A282B] bg-[#d4d6d8] dark:bg-[#1f1e1f]'
                                                        : 'border border-[#cf6161] bg-[#ed9cae] dark:border-[#b15454] dark:bg-[#C15C5C] text-[#611212]'
                                                } `}
                                                    value={approval.priority}
                                                    disabled
                                                >
                                                    <option value={''}>
                                                        Выбрать
                                                    </option>
                                                    <option value={0}>
                                                        Отсутствует
                                                    </option>
                                                    <option value={1}>
                                                        Важно
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    {approval.comment && (
                                        <div className="flex flex-col w-full gap-1 items-start justify-center">
                                            <div className="flex w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                Комментарий
                                            </div>
                                            <div className="flex w-full items-start justify-start">
                                                <textarea
                                                    className="form-textarea flex w-full"
                                                    maxLength={2000}
                                                    rows={2}
                                                    value={approval.comment}
                                                    disabled={true}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {expandedIndex === index &&
                                        (loading ? (
                                            <div className="flex flex-col w-full text-[#181818] dark:text-[#FFFFFF] items-center justify-center">
                                                <svg
                                                    className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                    ></path>
                                                </svg>
                                            </div>
                                        ) : approvers?.length > 0 ? (
                                            <>
                                                <div className="flex flex-row px-2 py-1 pt-3 text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                                                    Лист согласования
                                                </div>
                                                <div className="flex flex-col w-full items-center justify-center">
                                                    <div className="flex flex-row w-full gap-1 p-1 py-3 items-center justify-center rounded-t-md text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px] bg-[#d2d5d8] dark:bg-[#333033]">
                                                        <div className="flex w-10 items-center justify-center ">
                                                            №
                                                        </div>
                                                        <div className="flex flex-1 items-center justify-center ">
                                                            Согласующий
                                                        </div>
                                                        <div className="flex w-32 items-center justify-center ">
                                                            Дата
                                                        </div>
                                                        <div className="flex w-40 items-center justify-center ">
                                                            Виза
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col w-full gap-1 p-1 items-center justify-center rounded-b-md bg-[#e4e6e8] dark:bg-[#242225]">
                                                        {approvers.map(
                                                            (approver) => (
                                                                <div
                                                                    key={
                                                                        approver.guid
                                                                    }
                                                                    className="flex flex-col w-full gap-1 p-1 items-center justify-center rounded-md bg-[#f6f6f6] dark:bg-[#2A282B]"
                                                                >
                                                                    <div className="flex flex-row w-full gap-1 items-center justify-center text-[14px] ">
                                                                        <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                            {
                                                                                approver.user_position
                                                                            }
                                                                        </div>
                                                                        <div className="flex flex-1 flex-row gap-2 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                                                            {approver.is_saw ? (
                                                                                <HoverTooltip tooltipText="Просмотрено">
                                                                                    <div className="flex w-5 h-5 rounded-full items-center justify-center">
                                                                                        <svg
                                                                                            className="w-3 h-3"
                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                            viewBox="0 0 24 24"
                                                                                            id="trash-alt"
                                                                                        >
                                                                                            <path
                                                                                                fill={
                                                                                                    theme ==
                                                                                                    'dark'
                                                                                                        ? '#FFFFFF'
                                                                                                        : '#181818'
                                                                                                }
                                                                                                d="M21.92,11.6C19.9,6.91,16.1,4,12,4S4.1,6.91,2.08,11.6a1,1,0,0,0,0,.8C4.1,17.09,7.9,20,12,20s7.9-2.91,9.92-7.6A1,1,0,0,0,21.92,11.6ZM12,18c-3.17,0-6.17-2.29-7.9-6C5.83,8.29,8.83,6,12,6s6.17,2.29,7.9,6C18.17,15.71,15.17,18,12,18ZM12,8a4,4,0,1,0,4,4A4,4,0,0,0,12,8Zm0,6a2,2,0,1,1,2-2A2,2,0,0,1,12,14Z"
                                                                                            ></path>
                                                                                        </svg>
                                                                                    </div>
                                                                                </HoverTooltip>
                                                                            ) : (
                                                                                <HoverTooltip tooltipText="Не просмотрено">
                                                                                    <div className="flex w-5 h-5 rounded-full items-center justify-center">
                                                                                        <svg
                                                                                            className="w-3 h-3"
                                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                                            viewBox="0 0 24 24"
                                                                                            id="trash-alt"
                                                                                        >
                                                                                            <path
                                                                                                fill={
                                                                                                    theme ==
                                                                                                    'dark'
                                                                                                        ? '#FFFFFF'
                                                                                                        : '#181818'
                                                                                                }
                                                                                                d="M10.94,6.08A6.93,6.93,0,0,1,12,6c3.18,0,6.17,2.29,7.91,6a15.23,15.23,0,0,1-.9,1.64,1,1,0,0,0-.16.55,1,1,0,0,0,1.86.5,15.77,15.77,0,0,0,1.21-2.3,1,1,0,0,0,0-.79C19.9,6.91,16.1,4,12,4a7.77,7.77,0,0,0-1.4.12,1,1,0,1,0,.34,2ZM3.71,2.29A1,1,0,0,0,2.29,3.71L5.39,6.8a14.62,14.62,0,0,0-3.31,4.8,1,1,0,0,0,0,.8C4.1,17.09,7.9,20,12,20a9.26,9.26,0,0,0,5.05-1.54l3.24,3.25a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Zm6.36,9.19,2.45,2.45A1.81,1.81,0,0,1,12,14a2,2,0,0,1-2-2A1.81,1.81,0,0,1,10.07,11.48ZM12,18c-3.18,0-6.17-2.29-7.9-6A12.09,12.09,0,0,1,6.8,8.21L8.57,10A4,4,0,0,0,14,15.43L15.59,17A7.24,7.24,0,0,1,12,18Z"
                                                                                            ></path>
                                                                                        </svg>
                                                                                    </div>
                                                                                </HoverTooltip>
                                                                            )}
                                                                            {`${approver.last_name ? approver.last_name : ''} ${approver.first_name ? approver.first_name.charAt(0) + '.' : ''} ${approver.patronymic ? approver.patronymic.charAt(0) + '.' : ''}`}
                                                                        </div>
                                                                        <div className="flex w-40 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                                            {approver.approve_date
                                                                                ? approver.approve_date
                                                                                      .split(
                                                                                          '-'
                                                                                      )
                                                                                      .reverse()
                                                                                      .join(
                                                                                          '-'
                                                                                      )
                                                                                      .replace(
                                                                                          /[\-]/g,
                                                                                          '.'
                                                                                      )
                                                                                : '-'}
                                                                        </div>
                                                                        <div className="flex w-40 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                                            <input
                                                                                className={`flex px-2 py-1 rounded-md text-sm w-full items-center justify-center text-center 
                                                                            ${
                                                                                theme ==
                                                                                'dark'
                                                                                    ? approver.status ==
                                                                                      0
                                                                                        ? 'bg-[#FAD12E] text-[#3A2506]'
                                                                                        : approver.status ==
                                                                                            1
                                                                                          ? 'bg-[#049664] text-[#103917]'
                                                                                          : approver.status ==
                                                                                              2
                                                                                            ? 'bg-[#049664] text-[#103917]'
                                                                                            : approver.status ==
                                                                                                3
                                                                                              ? 'bg-[#C15C5C] text-[#611212]'
                                                                                              : ''
                                                                                    : approver.status ==
                                                                                        0
                                                                                      ? 'bg-[#FAD12E] text-[#3A2506]'
                                                                                      : approver.status ==
                                                                                          1
                                                                                        ? 'bg-[#83d2b7] text-[#103917]'
                                                                                        : approver.status ==
                                                                                            2
                                                                                          ? 'bg-[#83d2b7] text-[#103917]'
                                                                                          : approver.status ==
                                                                                              3
                                                                                            ? 'bg-[#ed9cae] text-[#611212]'
                                                                                            : ''
                                                                            }`}
                                                                                value={
                                                                                    approver.status ==
                                                                                    0
                                                                                        ? 'Согласование'
                                                                                        : approver.status ==
                                                                                            1
                                                                                          ? 'Согласовано'
                                                                                          : approver.status ==
                                                                                              2
                                                                                            ? 'Согласовано с замечаниями'
                                                                                            : approver.status ==
                                                                                                3
                                                                                              ? 'Не согласовано'
                                                                                              : ''
                                                                                }
                                                                                disabled
                                                                            ></input>
                                                                        </div>
                                                                    </div>

                                                                    {approver.comment && (
                                                                        <div className="flex flex-col w-full gap-1 items-start justify-center">
                                                                            <div className="flex w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                                                Комментарий
                                                                            </div>
                                                                            <div className="flex w-full items-start justify-start">
                                                                                <textarea
                                                                                    className="form-textarea flex w-full"
                                                                                    maxLength={
                                                                                        2000
                                                                                    }
                                                                                    rows={
                                                                                        2
                                                                                    }
                                                                                    value={
                                                                                        approver.comment
                                                                                    }
                                                                                    disabled={
                                                                                        true
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                                {approval.status == 0
                                                    ? userApprovers.map(
                                                          (
                                                              userApprove,
                                                              index2
                                                          ) =>
                                                              userApprove.status ==
                                                              0 ? (
                                                                  <>
                                                                      <div className="flex flex-row px-2 py-1 pt-3 text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                                                                          Резолюция
                                                                      </div>
                                                                      <div
                                                                          key={
                                                                              index2
                                                                          }
                                                                          className="flex flex-col w-full gap-3 p-2 items-start justify-start bg-[#e5e5e5] dark:bg-[#262427] rounded-md"
                                                                      >
                                                                          <div className="flex flex-row w-full gap-1 items-start justify-start text-[14px]">
                                                                              <div className="flex flex-col flex-1 w-full items-start justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                                                                  <div className="flex w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                                                      Согласующий
                                                                                  </div>
                                                                                  <div className="flex w-full items-start justify-start text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                                                                                      {`${userApprove.last_name} ${userApprove.first_name} ${userApprove.patronymic}`}
                                                                                  </div>
                                                                              </div>
                                                                              <div className="flex flex-col w-[165px] items-start justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                                                  <div className="flex w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                                                      Виза
                                                                                  </div>
                                                                                  <div className="flex w-full items-start justify-start">
                                                                                      <select
                                                                                          className={`form-select w-full`}
                                                                                          value={
                                                                                              selectedStatus ||
                                                                                              0
                                                                                          }
                                                                                          onChange={(
                                                                                              e
                                                                                          ) =>
                                                                                              setSelectedStatus(
                                                                                                  Number(
                                                                                                      e
                                                                                                          .target
                                                                                                          .value
                                                                                                  )
                                                                                              )
                                                                                          }
                                                                                      >
                                                                                          <option
                                                                                              value={
                                                                                                  0
                                                                                              }
                                                                                          >
                                                                                              Выбрать
                                                                                          </option>
                                                                                          <option
                                                                                              value={
                                                                                                  1
                                                                                              }
                                                                                          >
                                                                                              Согласовано
                                                                                          </option>
                                                                                          <option
                                                                                              value={
                                                                                                  2
                                                                                              }
                                                                                          >
                                                                                              Согласовано
                                                                                              с
                                                                                              замечаниями
                                                                                          </option>
                                                                                          <option
                                                                                              value={
                                                                                                  3
                                                                                              }
                                                                                          >
                                                                                              Не
                                                                                              согласовано
                                                                                          </option>
                                                                                      </select>
                                                                                  </div>
                                                                              </div>
                                                                          </div>
                                                                          <div className="flex flex-col w-full gap-1 items-start justify-start">
                                                                              <div className="flex w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                                                  Комментарий
                                                                              </div>
                                                                              <div className="flex w-full items-start justify-start">
                                                                                  <textarea
                                                                                      className="form-textarea flex w-full"
                                                                                      maxLength={
                                                                                          2000
                                                                                      }
                                                                                      rows={
                                                                                          2
                                                                                      }
                                                                                      value={
                                                                                          comment
                                                                                      }
                                                                                      onChange={(
                                                                                          e
                                                                                      ) => {
                                                                                          setComment(
                                                                                              e
                                                                                                  .target
                                                                                                  .value
                                                                                          )
                                                                                      }}
                                                                                  />
                                                                              </div>
                                                                          </div>

                                                                          <div className="flex flex-row w-full gap-1 items-center justify-end">
                                                                              <button
                                                                                  className={`flex gap-3 px-5 py-1 h-6 min-w-[120px] rounded-[10px] text-[12.8px] items-center justify-center ${!selectedStatus || saveLoading ? 'text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#EDEDED] dark:bg-[#494849]' : 'text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40] shadow-md shadow-[#18181830]'}`}
                                                                                  disabled={
                                                                                      !selectedStatus ||
                                                                                      saveLoading
                                                                                  }
                                                                                  onClick={() =>
                                                                                      saveApprovalResult(
                                                                                          userApprove.guid,
                                                                                          selectedStatus!,
                                                                                          comment
                                                                                      )
                                                                                  }
                                                                              >
                                                                                  Сохранить
                                                                              </button>
                                                                          </div>
                                                                      </div>
                                                                  </>
                                                              ) : (
                                                                  ''
                                                              )
                                                      )
                                                    : ''}
                                                <div className="flex flex-row w-full items-center justify-end">
                                                    <div className="flex flex-row w-full items-center justify-start gap-2 pt-2">
                                                        {(pos.user_guid ==
                                                            user?.object
                                                                .user_id ||
                                                            pos.supervisor_user_guid ==
                                                                user?.object
                                                                    .user_id) &&
                                                        approval.status == 0 &&
                                                        !activeApproverStatus ? (
                                                            <button
                                                                onClick={() =>
                                                                    handleApprovalDeletePopup(
                                                                        approval.guid
                                                                    )
                                                                }
                                                                className="flex flex-row px-2 min-w-[120px] h-6 rounded-[10px] items-center justify-center gap-1 shadow-md shadow-[#18181830] text-[#181818] bg-[#F6F6F6] hover:bg-[#ed9cae] dark:bg-[#494849] dark:hover:bg-[#701f1f] dark:text-[#FFFFFF]"
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="trash-alt"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                    ></path>
                                                                </svg>
                                                                <span className="text-[#181818] dark:text-[#FFFFFF] text-[12.8px]">
                                                                    Удалить
                                                                </span>
                                                            </button>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </div>

                                                    {pos.user_guid !=
                                                        user?.object.user_id &&
                                                    pos.supervisor_user_guid !=
                                                        user?.object.user_id &&
                                                    approval.status == 0 &&
                                                    approvers.some(
                                                        (approver) =>
                                                            approver.user_guid ===
                                                            user?.object.user_id
                                                    ) ? (
                                                        <div className="flex flex-row w-full items-center justify-end gap-2 pt-2">
                                                            <button
                                                                onClick={() => {
                                                                    const addGuid =
                                                                        String(
                                                                            approval.guid
                                                                        ) // Локальная переменная
                                                                    setApprovalAddGuid(
                                                                        addGuid
                                                                    )
                                                                    setApprovalChangeGuid(
                                                                        ''
                                                                    )
                                                                    setApproversAdd(
                                                                        []
                                                                    )
                                                                    handleApprovalPopup()
                                                                }}
                                                                className="flex flex-row px-2 w-[190px] h-6 rounded-[10px] items-center justify-center gap-1 shadow-md shadow-[#18181830] text-xs bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="add-approver"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                                    ></path>
                                                                </svg>
                                                                <span className="text-[#181818] dark:text-[#FFFFFF] text-[12.8px]">
                                                                    Добавить
                                                                    согласующего
                                                                </span>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )}

                                                    {(pos.user_guid ==
                                                        user?.object.user_id ||
                                                        pos.supervisor_user_guid ==
                                                            user?.object
                                                                .user_id) &&
                                                    approval.status == 0 ? (
                                                        <div className="flex flex-row w-full items-center justify-end gap-2 pt-2">
                                                            <button
                                                                onClick={() => {
                                                                    const changeGuid =
                                                                        String(
                                                                            approval.guid
                                                                        ) // Локальная переменная
                                                                    setApprovalChangeGuid(
                                                                        changeGuid
                                                                    )
                                                                    setApprovalAddGuid(
                                                                        ''
                                                                    )
                                                                    setApproversAdd(
                                                                        []
                                                                    )
                                                                    handleApprovalPopup()
                                                                }}
                                                                className="flex flex-row px-5 min-w-[120px] h-6 rounded-[10px] items-center justify-center gap-1 shadow-md shadow-[#18181830] text-xs bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="change-approval"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M5,18H9.24a1,1,0,0,0,.71-.29l6.92-6.93h0L19.71,8a1,1,0,0,0,0-1.42L15.47,2.29a1,1,0,0,0-1.42,0L11.23,5.12h0L4.29,12.05a1,1,0,0,0-.29.71V17A1,1,0,0,0,5,18ZM14.76,4.41l2.83,2.83L16.17,8.66,13.34,5.83ZM6,13.17l5.93-5.93,2.83,2.83L8.83,16H6ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                                                    ></path>
                                                                </svg>
                                                                <span className="text-[#181818] dark:text-[#FFFFFF] text-[12.8px]">
                                                                    Редактировать
                                                                </span>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="text-[#6A6A6A] dark:text-[#8A8A8A] text-center text-[12.8px]">
                                                    Согласующие отсутствуют
                                                </div>
                                                <div className="flex flex-row w-full items-center justify-end">
                                                    <div className="flex flex-row w-full items-center justify-start gap-2 pt-2">
                                                        {(pos.user_guid ==
                                                            user?.object
                                                                .user_id ||
                                                            pos.supervisor_user_guid ==
                                                                user?.object
                                                                    .user_id) &&
                                                        approval.status == 0 &&
                                                        !activeApproverStatus ? (
                                                            <button
                                                                onClick={() =>
                                                                    handleApprovalDeletePopup(
                                                                        approval.guid
                                                                    )
                                                                }
                                                                className="flex flex-row px-2 min-w-[120px] h-6 rounded-[10px] items-center justify-center gap-1 shadow-md shadow-[#18181830] text-[#181818] bg-[#F6F6F6] hover:bg-[#ed9cae] dark:bg-[#494849] dark:hover:bg-[#701f1f] dark:text-[#FFFFFF]"
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="trash-alt"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                    ></path>
                                                                </svg>
                                                                <span className="text-[#181818] dark:text-[#FFFFFF] text-[12.8px]">
                                                                    Удалить
                                                                </span>
                                                            </button>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </div>

                                                    {(pos.user_guid !=
                                                        user?.object.user_id ||
                                                        pos.supervisor_user_guid !=
                                                            user?.object
                                                                .user_id) &&
                                                    approval.status == 0 &&
                                                    approvers.some(
                                                        (approver) =>
                                                            approver.user_guid ===
                                                            user?.object.user_id
                                                    ) ? (
                                                        <div className="flex flex-row w-full items-center justify-end gap-2 pt-2">
                                                            <button
                                                                onClick={() => {
                                                                    const addGuid =
                                                                        String(
                                                                            approval.guid
                                                                        ) // Локальная переменная
                                                                    setApprovalAddGuid(
                                                                        addGuid
                                                                    )
                                                                    setApprovalChangeGuid(
                                                                        ''
                                                                    )
                                                                    setApproversAdd(
                                                                        []
                                                                    )
                                                                    handleApprovalPopup()
                                                                }}
                                                                className="flex flex-row px-2 min-w-[120px] h-6 rounded-[10px] items-center justify-center gap-1 shadow-md shadow-[#18181830] text-xs bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="add-approver"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                                    ></path>
                                                                </svg>
                                                                <span className="text-[#181818] dark:text-[#FFFFFF] text-[12.8px]">
                                                                    Добавить
                                                                    согласующего
                                                                </span>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )}

                                                    {(pos.user_guid ==
                                                        user?.object.user_id ||
                                                        pos.supervisor_user_guid ==
                                                            user?.object
                                                                .user_id) &&
                                                    approval.status == 0 ? (
                                                        <div className="flex flex-row w-full items-center justify-end gap-2 pt-2">
                                                            <button
                                                                onClick={() => {
                                                                    const changeGuid =
                                                                        String(
                                                                            approval.guid
                                                                        ) // Локальная переменная
                                                                    setApprovalChangeGuid(
                                                                        changeGuid
                                                                    )
                                                                    setApprovalAddGuid(
                                                                        ''
                                                                    )
                                                                    setApproversAdd(
                                                                        []
                                                                    )
                                                                    handleApprovalPopup()
                                                                }}
                                                                className="flex flex-row px-5 min-w-[120px] h-6 rounded-[10px] items-center justify-center gap-1 shadow-md shadow-[#18181830] text-xs bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="change-approval"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M5,18H9.24a1,1,0,0,0,.71-.29l6.92-6.93h0L19.71,8a1,1,0,0,0,0-1.42L15.47,2.29a1,1,0,0,0-1.42,0L11.23,5.12h0L4.29,12.05a1,1,0,0,0-.29.71V17A1,1,0,0,0,5,18ZM14.76,4.41l2.83,2.83L16.17,8.66,13.34,5.83ZM6,13.17l5.93-5.93,2.83,2.83L8.83,16H6ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                                                    ></path>
                                                                </svg>
                                                                <span className="text-[#181818] dark:text-[#FFFFFF] text-[12.8px]">
                                                                    Редактировать
                                                                </span>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                            </>
                                        ))}
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className="flex flex-col w-full rounded-lg p-3 gap-3 bg-[#dbdbdb] dark:bg-[#333033] items-center justify-start">
                            <div className="flex flex-col w-full bg-[#dbdbdb] dark:bg-[#333033] items-center justify-start">
                                <div className="flex w-full items-center justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Создано
                                </div>
                                <div className="flex flex-row w-full gap-3 items-center justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    {getNameUser(pos.user_guid) ==
                                    'undefined undefined undefined'
                                        ? '-'
                                        : getNameUser(pos.user_guid)}
                                    <span className="text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        {formatDateTime(pos.sys_created_at)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-row w-full gap-2 bg-[#dbdbdb] dark:bg-[#333033] items-center justify-start">
                                <div className="flex flex-col w-fit">
                                    <div className="flex w-full items-center justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Ответственный
                                    </div>
                                    <div className="flex w-full items-center justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                        {getNameUser(
                                            pos.supervisor_user_guid
                                        ) == 'undefined undefined undefined'
                                            ? '-'
                                            : getNameUser(
                                                  pos.supervisor_user_guid
                                              )}
                                    </div>
                                </div>
                                <div className="flex h-8 w-8 mt-4 items-center justify-center text-center">
                                    {(pos.user_guid == user?.object.user_id ||
                                        pos.supervisor_user_guid ==
                                            user?.object.user_id ||
                                        isUserRoleName == 'admin' ||
                                        isUserRoleName == 'specialist' ||
                                        isUserRoleName == 'curator') && (
                                        <HoverTooltip tooltipText="Изменить">
                                            <button
                                                onClick={
                                                    handleChangeSupervisorPopup
                                                }
                                                className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    id="edit-alt"
                                                >
                                                    <path
                                                        fill={
                                                            theme == 'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                        }
                                                        d="M5,18H9.24a1,1,0,0,0,.71-.29l6.92-6.93h0L19.71,8a1,1,0,0,0,0-1.42L15.47,2.29a1,1,0,0,0-1.42,0L11.23,5.12h0L4.29,12.05a1,1,0,0,0-.29.71V17A1,1,0,0,0,5,18ZM14.76,4.41l2.83,2.83L16.17,8.66,13.34,5.83ZM6,13.17l5.93-5.93,2.83,2.83L8.83,16H6ZM21,20H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </HoverTooltip>
                                    )}
                                </div>
                            </div>
                            {changeSupervisorPopup && (
                                <div
                                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                                    onClick={handleChangeSupervisorPopup}
                                >
                                    <div
                                        className="flex flex-col p-5 rounded-lg w-[800px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                                            Реестр пользователей
                                        </h2>
                                        <div className="flex flex-row w-full min-h-10 p-1 gap-1 rounded-md items-center justify-start flex-wrap bg-[#FFFFFF] dark:bg-[#181818]">
                                            {selectedSupervisor == '' ||
                                            selectedSupervisor == null ? (
                                                ''
                                            ) : (
                                                <div className="flex flex-row w-fit h-8 p-1 pl-2 gap-2 rounded-md items-center justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#ebebeb] dark:bg-[#282629]">
                                                    {getNameUser(
                                                        selectedSupervisor
                                                    )}
                                                    <HoverTooltip tooltipText="Удалить пльзователя">
                                                        <button
                                                            onClick={() =>
                                                                handleCheckboxChangeSupervisor(
                                                                    ''
                                                                )
                                                            }
                                                            className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                        >
                                                            <svg
                                                                className="w-3 h-3"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 24 24"
                                                                id="trash-alt"
                                                            >
                                                                <path
                                                                    fill={
                                                                        theme ==
                                                                        'dark'
                                                                            ? '#FFFFFF'
                                                                            : '#181818'
                                                                    }
                                                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                ></path>
                                                            </svg>
                                                        </button>
                                                    </HoverTooltip>
                                                </div>
                                            )}

                                            <input
                                                className="flex p-1 min-w-40 h-8 items-center justify-start focus:outline-none text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] dark:bg-[#181818]"
                                                value={userFilter}
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        e.target.value
                                                    )
                                                }
                                            ></input>
                                        </div>
                                        <div className="flex flex-col w-full h-fit items-center justify-center">
                                            <div className="flex flex-row w-full gap-1 p-1 py-3 items-center justify-center rounded-t-md text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                                <div className="flex w-5 items-center justify-center "></div>
                                                <div className="flex w-10 items-center justify-center ">
                                                    №
                                                </div>
                                                <div className="flex flex-1 items-center justify-center ">
                                                    Фамилия Имя Отчество
                                                </div>
                                                <div className="flex w-48 items-center justify-center ">
                                                    Подразделение
                                                </div>
                                                <div className="flex w-48 items-center justify-center ">
                                                    Должность
                                                </div>
                                            </div>
                                            <div className="flex flex-col w-full h-[400px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                                {userFilteredList.map(
                                                    (item, index) => (
                                                        <div
                                                            key={item.guid}
                                                            className="flex flex-col w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                            onClick={() => {
                                                                setUserFilter([
                                                                    '',
                                                                ])
                                                                handleCheckboxChangeSupervisor(
                                                                    String(
                                                                        item.guid
                                                                    )
                                                                )
                                                            }}
                                                        >
                                                            <div className="flex flex-row w-full gap-1 items-center justify-center text-[14px] ">
                                                                <div className="flex w-5 items-center justify-center ">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={
                                                                            selectedSupervisor ==
                                                                                '' ||
                                                                            selectedSupervisor ==
                                                                                null
                                                                                ? false
                                                                                : selectedSupervisor.includes(
                                                                                      String(
                                                                                          item.guid
                                                                                      )
                                                                                  )
                                                                        }
                                                                        onChange={() => {
                                                                            if (
                                                                                item?.guid !=
                                                                                null
                                                                            ) {
                                                                                handleCheckboxChangeSupervisor(
                                                                                    String(
                                                                                        item.guid
                                                                                    )
                                                                                ) // Преобразование в строку
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                    {index + 1}
                                                                </div>
                                                                <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                                                    {`${item.last_name ? item.last_name : ''} ${item.first_name ? item.first_name : ''} ${item.patronymic ? item.patronymic : ''}`}
                                                                </div>
                                                                <div className="flex w-48 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                                    {getNameStruct(
                                                                        item?.struct_guid ||
                                                                            ''
                                                                    )}
                                                                </div>
                                                                <div className="flex w-48 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                                    {
                                                                        item.position
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-row text-right justify-end items-center gap-2">
                                            <button
                                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                                onClick={
                                                    handleChangeSupervisorPopup
                                                }
                                            >
                                                Отмена
                                            </button>
                                            <button
                                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                                onClick={handleSupervisorChange}
                                            >
                                                {isApprovalsAdd ? (
                                                    <svg
                                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                        ></path>
                                                    </svg>
                                                ) : (
                                                    'Изменить'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {approvalDeletePopup && (
                    <div
                        className="fixed top-0 left-0 w-full h-full bg-[#00000030] flex justify-center items-center"
                        onClick={() => handleApprovalDeletePopup('')}
                    >
                        <div
                            className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                                <div className="flex flex-col">
                                    <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                        Удалить согласование?
                                    </label>
                                </div>
                                <div className="flex w-full flex-row items-center justify-end gap-3">
                                    <button
                                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        onClick={() =>
                                            handleApprovalDeletePopup('')
                                        }
                                    >
                                        Отмена
                                    </button>
                                    <button
                                        className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                        onClick={() => handleApprovalDelete()}
                                    >
                                        Удалить
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {approvalCreatePopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-[#000000] bg-opacity-50"
                    onClick={() => {
                        setApproversAdd([])
                        setApprovalChangeGuid('')
                        setApprovalAddGuid('')
                        handleApprovalPopup()
                    }}
                >
                    <div
                        className="flex flex-col p-5 rounded-lg w-[700px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                            {approvalChangeGuid
                                ? 'Изменение согласования'
                                : approvalAddGuid
                                  ? 'Добавление согласующего'
                                  : 'Создание согласования'}
                        </h2>
                        <div className="flex flex-row w-full gap-3 items-center justify-start">
                            <div className="flex flex-col w-full gap-1 items-start justify-start">
                                <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Начало согласования
                                </div>
                                <div className="flex w-full items-start justify-start">
                                    <input
                                        type="date"
                                        style={{
                                            colorScheme:
                                                theme == 'dark'
                                                    ? 'dark'
                                                    : 'light',
                                        }}
                                        className="form-input w-full bg-[#1F1E1F]"
                                        value={approvalStartDate}
                                        onChange={(e) =>
                                            setApprovalStartDate(e.target.value)
                                        }
                                        disabled={
                                            approvalAddGuid ? true : false
                                        }
                                    ></input>
                                </div>
                            </div>
                            <div className="flex flex-col w-full gap-1 items-start justify-start">
                                <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                    Срок окончания
                                </div>
                                <div className="flex w-full items-start justify-start">
                                    <input
                                        type="date"
                                        style={{
                                            colorScheme:
                                                theme == 'dark'
                                                    ? 'dark'
                                                    : 'light',
                                        }}
                                        className="form-input w-full bg-[#1F1E1F]"
                                        value={approvalEndDate}
                                        onChange={(e) =>
                                            setApprovalEndDate(e.target.value)
                                        }
                                        disabled={
                                            approvalAddGuid ? true : false
                                        }
                                    ></input>
                                </div>
                            </div>
                            <div className="flex flex-col w-full gap-1 items-start justify-start">
                                <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Приоритет
                                </div>
                                <div className="flex w-full items-start justify-start">
                                    <select
                                        className={`form-select w-full `}
                                        value={approvalPriority}
                                        onChange={(e) =>
                                            setApprovalPriority(
                                                Number(e.target.value)
                                            )
                                        }
                                        disabled={
                                            approvalAddGuid ? true : false
                                        }
                                    >
                                        <option value={-1}>Выбрать</option>
                                        <option value={0}>Отсутствует</option>
                                        <option value={1}>Важно</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col w-full gap-1 items-start justify-center">
                            <div className="flex w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Комментарий
                            </div>
                            <div className="flex w-full items-start justify-start">
                                <textarea
                                    className="form-textarea flex w-full"
                                    maxLength={2000}
                                    rows={2}
                                    value={approvalComment}
                                    onChange={(e) => {
                                        setApprovalComment(e.target.value)
                                    }}
                                    disabled={approvalAddGuid ? true : false}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col w-full items-center justify-center">
                            <div className="flex flex-row w-full gap-1 py-2 px-2 items-center justify-start rounded-t-md text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                <button
                                    onClick={handleAddApproversPopup}
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                >
                                    <svg
                                        className="plus-icon"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                    Добавить согласующего
                                </button>
                            </div>
                            <div className="flex flex-row w-full gap-1 p-1 py-3 items-center justify-center text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2A282B]">
                                <div className="flex w-10 items-center justify-center ">
                                    №
                                </div>
                                <div className="flex flex-1 items-center justify-center ">
                                    Согласующий
                                </div>
                                <div className="flex w-32 items-center justify-center ">
                                    Дата
                                </div>
                                <div className="flex w-40 items-center justify-center ">
                                    Виза
                                </div>
                                <div className="flex w-10 items-center justify-center "></div>
                            </div>
                            <div className="flex flex-col w-full h-[350px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto bg-[#e4e6e8] dark:bg-[#242225] custom-scrollbar">
                                {approversAdd?.length > 0 ? (
                                    approversAdd.map((approver, index3) => (
                                        <div
                                            key={index3}
                                            className="flex flex-col w-full h-fit gap-1 p-1 items-start justify-start rounded-md text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                        >
                                            <div className="flex flex-row w-full h-fit gap-1 items-center justify-center text-[14px] ">
                                                <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                    {approver.user_position}
                                                </div>
                                                <div className="flex flex-1 flex-row gap-2 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                                    {approver.is_saw ? (
                                                        <HoverTooltip tooltipText="Просмотрено">
                                                            <div className="flex w-5 h-5 rounded-full items-center justify-center">
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="trash-alt"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M21.92,11.6C19.9,6.91,16.1,4,12,4S4.1,6.91,2.08,11.6a1,1,0,0,0,0,.8C4.1,17.09,7.9,20,12,20s7.9-2.91,9.92-7.6A1,1,0,0,0,21.92,11.6ZM12,18c-3.17,0-6.17-2.29-7.9-6C5.83,8.29,8.83,6,12,6s6.17,2.29,7.9,6C18.17,15.71,15.17,18,12,18ZM12,8a4,4,0,1,0,4,4A4,4,0,0,0,12,8Zm0,6a2,2,0,1,1,2-2A2,2,0,0,1,12,14Z"
                                                                    ></path>
                                                                </svg>
                                                            </div>
                                                        </HoverTooltip>
                                                    ) : (
                                                        <HoverTooltip tooltipText="Не просмотрено">
                                                            <div className="flex w-5 h-5 rounded-full items-center justify-center">
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="trash-alt"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M10.94,6.08A6.93,6.93,0,0,1,12,6c3.18,0,6.17,2.29,7.91,6a15.23,15.23,0,0,1-.9,1.64,1,1,0,0,0-.16.55,1,1,0,0,0,1.86.5,15.77,15.77,0,0,0,1.21-2.3,1,1,0,0,0,0-.79C19.9,6.91,16.1,4,12,4a7.77,7.77,0,0,0-1.4.12,1,1,0,1,0,.34,2ZM3.71,2.29A1,1,0,0,0,2.29,3.71L5.39,6.8a14.62,14.62,0,0,0-3.31,4.8,1,1,0,0,0,0,.8C4.1,17.09,7.9,20,12,20a9.26,9.26,0,0,0,5.05-1.54l3.24,3.25a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Zm6.36,9.19,2.45,2.45A1.81,1.81,0,0,1,12,14a2,2,0,0,1-2-2A1.81,1.81,0,0,1,10.07,11.48ZM12,18c-3.18,0-6.17-2.29-7.9-6A12.09,12.09,0,0,1,6.8,8.21L8.57,10A4,4,0,0,0,14,15.43L15.59,17A7.24,7.24,0,0,1,12,18Z"
                                                                    ></path>
                                                                </svg>
                                                            </div>
                                                        </HoverTooltip>
                                                    )}
                                                    {getNameUser(
                                                        approver.user_guid
                                                    )}
                                                </div>
                                                <div className="flex w-32 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                    {approver.approve_date
                                                        ? approver.approve_date
                                                              .split('-')
                                                              .reverse()
                                                              .join('-')
                                                              .replace(
                                                                  /[\-]/g,
                                                                  '.'
                                                              )
                                                        : '-'}
                                                </div>
                                                <div className="flex w-40 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                    <input
                                                        className={`flex px-2 py-1 rounded-md border-0 text-sm w-full items-center justify-center text-center 
                                                                            ${
                                                                                theme ==
                                                                                'dark'
                                                                                    ? approver.status ==
                                                                                      0
                                                                                        ? 'bg-[#FAD12E] text-[#3A2506]'
                                                                                        : approver.status ==
                                                                                            1
                                                                                          ? 'bg-[#049664] text-[#103917]'
                                                                                          : approver.status ==
                                                                                              2
                                                                                            ? 'bg-[#049664] text-[#103917]'
                                                                                            : approver.status ==
                                                                                                3
                                                                                              ? 'bg-[#C15C5C] text-[#611212]'
                                                                                              : ''
                                                                                    : approver.status ==
                                                                                        0
                                                                                      ? 'bg-[#FAD12E] text-[#3A2506]'
                                                                                      : approver.status ==
                                                                                          1
                                                                                        ? 'bg-[#83d2b7] text-[#103917]'
                                                                                        : approver.status ==
                                                                                            2
                                                                                          ? 'bg-[#83d2b7] text-[#103917]'
                                                                                          : approver.status ==
                                                                                              3
                                                                                            ? 'bg-[#ed9cae] text-[#611212]'
                                                                                            : ''
                                                                            }`}
                                                        value={
                                                            approver.status == 0
                                                                ? 'Согласование'
                                                                : approver.status ==
                                                                    1
                                                                  ? 'Согласовано'
                                                                  : approver.status ==
                                                                      2
                                                                    ? 'Согласовано с замечаниями'
                                                                    : approver.status ==
                                                                        3
                                                                      ? 'Не согласовано'
                                                                      : ''
                                                        }
                                                        disabled
                                                    ></input>
                                                </div>
                                                <div className="flex w-10 items-center justify-center ">
                                                    {approvalAddGuid ? (
                                                        approver.add_user_guid ==
                                                        user?.object.user_id ? (
                                                            approver.status ==
                                                            0 ? (
                                                                <HoverTooltip tooltipText="Удалить пльзователя">
                                                                    <button
                                                                        onClick={() => {
                                                                            if (
                                                                                approvalAddGuid
                                                                            ) {
                                                                                deleteApprovers(
                                                                                    String(
                                                                                        approver.guid
                                                                                    )
                                                                                )
                                                                                handleRemoveApprover(
                                                                                    approver.user_guid
                                                                                )
                                                                            } else {
                                                                                handleRemoveApprover(
                                                                                    approver.user_guid
                                                                                )
                                                                            }
                                                                        }}
                                                                        className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                                    >
                                                                        <svg
                                                                            className="w-3 h-3"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            viewBox="0 0 24 24"
                                                                            id="trash-alt"
                                                                        >
                                                                            <path
                                                                                fill={
                                                                                    theme ==
                                                                                    'dark'
                                                                                        ? '#FFFFFF'
                                                                                        : '#181818'
                                                                                }
                                                                                d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                            ></path>
                                                                        </svg>
                                                                    </button>
                                                                </HoverTooltip>
                                                            ) : (
                                                                ''
                                                            )
                                                        ) : (
                                                            ''
                                                        )
                                                    ) : approver.status == 0 ? (
                                                        <HoverTooltip tooltipText="Удалить пльзователя">
                                                            <button
                                                                onClick={() => {
                                                                    if (
                                                                        approvalChangeGuid
                                                                    ) {
                                                                        deleteApprovers(
                                                                            String(
                                                                                approver.guid
                                                                            )
                                                                        )
                                                                        handleRemoveApprover(
                                                                            approver.user_guid
                                                                        )
                                                                    } else {
                                                                        handleRemoveApprover(
                                                                            approver.user_guid
                                                                        )
                                                                    }
                                                                }}
                                                                className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                            >
                                                                <svg
                                                                    className="w-3 h-3"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    id="trash-alt"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                    ></path>
                                                                </svg>
                                                            </button>
                                                        </HoverTooltip>
                                                    ) : (
                                                        ''
                                                    )}
                                                </div>
                                            </div>

                                            {approver.comment && (
                                                <div className="flex flex-col w-full gap-1 items-start justify-center">
                                                    <div className="flex w-full items-start justify-start text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                                        Комментарий
                                                    </div>
                                                    <div className="flex w-full items-start justify-start">
                                                        <textarea
                                                            className="form-textarea flex w-full"
                                                            maxLength={2000}
                                                            rows={2}
                                                            value={
                                                                approver.comment
                                                            }
                                                            disabled={true}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex w-full h-fit text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px] items-center justify-center">
                                        Согласующие отсутствуют
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end gap-2">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={() => {
                                    setApproversAdd([])
                                    setApprovalChangeGuid('')
                                    setApprovalAddGuid('')
                                    handleApprovalPopup()
                                }}
                            >
                                Отмена
                            </button>
                            <button
                                className={`flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs  ${approversAdd.length < 1 ? 'text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dcdcdc] dark:bg-[#494849]' : 'text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40] shadow-md shadow-[#18181830]'}`}
                                onClick={() => {
                                    approvalChangeGuid
                                        ? handleApprovalChange()
                                        : approvalAddGuid
                                          ? handleApprovalAdd()
                                          : handleApprovalCreate()
                                }}
                                disabled={approversAdd.length < 1}
                            >
                                {isApprovalCreate ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : approvalChangeGuid ? (
                                    'Сохранить'
                                ) : approvalAddGuid ? (
                                    'Сохранить'
                                ) : (
                                    'Создать'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {addApproversPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handleAddApproversPopup}
                >
                    <div
                        className="flex flex-col p-5 rounded-lg w-[800px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                            Реестр пользователей
                        </h2>
                        <div className="flex flex-row w-full min-h-10 p-1 gap-1 rounded-md items-center justify-start flex-wrap bg-[#FFFFFF] dark:bg-[#181818]">
                            {selectedItems.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex flex-row w-fit h-8 p-1 pl-2 gap-2 rounded-md items-center justify-start text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#ebebeb] dark:bg-[#282629]"
                                >
                                    {getNameUser(item)}
                                    <HoverTooltip tooltipText="Удалить пльзователя">
                                        <button
                                            onClick={() =>
                                                handleCheckboxChange(item)
                                            }
                                            className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                        >
                                            <svg
                                                className="w-3 h-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                id="trash-alt"
                                            >
                                                <path
                                                    fill={
                                                        theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                    }
                                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                ></path>
                                            </svg>
                                        </button>
                                    </HoverTooltip>
                                </div>
                            ))}
                            <input
                                className="flex p-1 min-w-40 h-8 items-center justify-start focus:outline-none text-[12.8px] text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] dark:bg-[#181818]"
                                value={userFilter}
                                onChange={(e) =>
                                    handleInputChange(e.target.value)
                                }
                            ></input>
                        </div>
                        <div className="flex flex-col w-full h-fit items-center justify-center">
                            <div className="flex flex-row w-full gap-1 p-1 py-3 items-center justify-center rounded-t-md text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                <div className="flex w-5 items-center justify-center "></div>
                                <div className="flex w-10 items-center justify-center ">
                                    №
                                </div>
                                <div className="flex flex-1 items-center justify-center ">
                                    Фамилия Имя Отчество
                                </div>
                                <div className="flex w-48 items-center justify-center ">
                                    Подразделение
                                </div>
                                <div className="flex w-48 items-center justify-center ">
                                    Должность
                                </div>
                            </div>
                            <div className="flex flex-col w-full h-[400px] gap-1 p-1 items-start justify-start rounded-b-md overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                {userFilteredList.map((item, index) => (
                                    <div
                                        key={item.guid}
                                        className="flex flex-col w-full h-fit gap-1 p-1 items-center justify-center rounded-md cursor-pointer text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                        onClick={() => {
                                            setUserFilter([''])
                                            handleCheckboxChange(
                                                String(item.guid)
                                            )
                                        }}
                                    >
                                        <div className="flex flex-row w-full gap-1 items-center justify-center text-[14px] ">
                                            <div className="flex w-5 items-center justify-center ">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.includes(
                                                        String(item.guid)
                                                    )}
                                                    onChange={() => {
                                                        if (
                                                            item?.guid != null
                                                        ) {
                                                            handleCheckboxChange(
                                                                String(
                                                                    item.guid
                                                                )
                                                            ) // Преобразование в строку
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                {index + 1}
                                            </div>
                                            <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                                {`${item.last_name ? item.last_name : ''} ${item.first_name ? item.first_name : ''} ${item.patronymic ? item.patronymic : ''}`}
                                            </div>
                                            <div className="flex w-48 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                {getNameStruct(
                                                    item?.struct_guid || ''
                                                )}
                                            </div>
                                            <div className="flex w-48 items-center justify-center text-[#181818] dark:text-[#FFFFFF] ">
                                                {item.position}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-row text-right justify-end items-center gap-2">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleAddApproversPopup}
                            >
                                Отмена
                            </button>
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                onClick={handlePreAddApproval}
                            >
                                {isApprovalsAdd ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Добавить'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {errorPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50"
                    onClick={handleErrorPopup}
                >
                    <div
                        className={`p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] ${
                            !isHaveError ? 'w-[500px]' : 'w-[500px]'
                        }  h-fit`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            {!isHaveError ? (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#187A33"
                                        d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,.71.29,1,1,0,0,0,.7-.29l5-5a1,1,0,0,0,0-1.42A1,1,0,0,0,14.72,8.79ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                                    ></path>
                                </svg>
                            ) : (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#C15C5C"
                                        d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                    ></path>
                                </svg>
                            )}

                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                {!isHaveError
                                    ? 'Проверка прошла успешно'
                                    : 'Найдены ошибки'}
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            {!isHaveError ? (
                                <div className="flex flex-col w-full space-y-1">
                                    <div>
                                        <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                            Ошибок нет. Для дальнейшей работы
                                            нажмите на кнопку «Готово».
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col w-full space-y-1 gap-2">
                                    <div className="flex flex-col mt-2 space-y-1">
                                        <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                            Внимание! Для дальнейшей работы
                                            необходимо исправить ошибки в
                                            следующих полях:
                                        </label>
                                    </div>
                                    <div className="flex flex-col mt-2 space-y-1">
                                        <textarea
                                            className="form-textarea flex w-full h-full text-[11px] items-start justify-start p-2 rounded-md border-0"
                                            value={getErrorsText(errors)}
                                            rows={21}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleErrorPopup}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer
                user_guid={user?.object.user_id}
                regNumber={reg_number}
                position={pos}
                approve={isApprove}
                isUserRoleName={isUserRoleName}
                isSendPlan={isSendPlan}
                handleSave={handleSave}
                onPositionStatusChange={(posStatus) => {
                    setPos((prev) => ({
                        ...prev,
                        position_status: posStatus,
                    }))
                }}
                onPositionStageChange={(posStage) => {
                    setPos((prev) => ({
                        ...prev,
                        position_stage: posStage,
                    }))
                }}
                handleValidation={handleValidation}
                isHaveError={isHaveError}
                theme={theme}
                lastApprovalStatus={lastApprovalStatus}
                lastApprovalStatusAccept={lastApprovalStatusAccept}
                organization={orgData}
            />
        </div>
    )
}

export default PlanPositionEditPage
